import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import Fuse from 'fuse.js';
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { ZoneGpsPicker } from '../components/map/ZoneGpsPicker';
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Combobox } from "../components/ui/combobox";
import LocationHierarchyManager from "../components/LocationHierarchyManager";
import { Search, Plus, X, Copy, Trash2, Building, ChevronDown, MapPinOff } from 'lucide-react';
import LocationDepartmentManagement from '@/components/LocationDepartmentManagement';
import IncidentOriginChip from '@/components/IncidentOriginChip';
import { CommanServices } from '@/services/commanServices';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from '@/components/ui/alert-dialog';

// Global Department Service - Single Source of Truth with Performance Optimization
const DepartmentService = {
  departments: [],
  listeners: [],
  lastFetch: 0,
  fetchTimeout: null,
  CACHE_DURATION: 30000, // 30 seconds cache
  DEBOUNCE_DELAY: 500, // 500ms debounce

  // Add listener for real-time updates
  addListener(callback) {
    this.listeners.push(callback);
  },

  // Remove listener
  removeListener(callback) {
    this.listeners = this.listeners.filter(l => l !== callback);
  },

  // Notify all listeners of department changes
  notify() {
    this.listeners.forEach(callback => callback(this.departments));
  },

  // Debounced fetch departments to prevent excessive api calls
  async fetchDepartments() {
    return new Promise((resolve) => {
      // Clear existing timeout
      if (this.fetchTimeout) {
        clearTimeout(this.fetchTimeout);
      }

      // Set new debounced timeout
      this.fetchTimeout = setTimeout(async () => {
        const result = await this._actualFetchDepartments();
        resolve(result);
      }, this.DEBOUNCE_DELAY);
    });
  },

  // Actual fetch implementation with caching
  async _actualFetchDepartments() {
    try {
      const now = Date.now();

      // Return cached data if still valid
      if (this.departments.length > 0 && (now - this.lastFetch) < this.CACHE_DURATION) {
        console.log('🚀 DEPARTMENT SERVICE: Using cached departments (performance optimization)');
        this.notify();
        return this.departments;
      }

      console.log('🔗 DEPARTMENT SERVICE: Fetching departments with location counts (optimized single query)');

      // PERFORMANCE FIX: Use single optimized endpoint instead of N+1 queries
      const response = await CommanServices.departmentslocationcounts;

      if (response.status) {
        // Filter out null/invalid departments
        const validDepartments = (response.data || []).filter(dept =>
          dept &&
          dept.id &&
          dept.department_name &&
          typeof dept.department_name === 'string'
        );

        // CRITICAL FIX: VERSIONED MERGE for departments (mirrors Primary Toggle zones pattern)
        if (this.departments.length === 0) {
          // Initial load - use all fresh data
          this.departments = validDepartments;
        } else {
          // Create maps for efficient lookup
          const prevDepartmentsMap = new Map(this.departments.map(d => [d.id, d]));
          const mergedDepartments = [];

          validDepartments.forEach(freshDepartment => {
            const prevDepartment = prevDepartmentsMap.get(freshDepartment.id);

            if (!prevDepartment) {
              // New department - use fresh data
              mergedDepartments.push(freshDepartment);
              return;
            }

            // VERSIONED MERGE: Only update leader_count if server version is newer AND has valid counts
            const serverVersion = freshDepartment.membersVersion || 0;
            const localVersion = prevDepartment.membersVersion || 0;

            // Check if fresh data has DEFINED leader_count field (mirrors Primary Toggle count check)
            const hasValidLeaderCount = typeof freshDepartment.leader_count === 'number';

            if (hasValidLeaderCount && serverVersion >= localVersion) {
              // Server data is newer and has valid leader count - use server data
              console.debug(`[departments:merge] id=${freshDepartment.id?.substring(0, 8)}... local.v=${localVersion} server.v=${serverVersion} action=applied leader_count=${freshDepartment.leader_count}`);
              mergedDepartments.push(freshDepartment);
            } else if (!hasValidLeaderCount && typeof prevDepartment.leader_count === 'number') {
              // Server data is missing leader count but local has valid count - preserve local
              console.debug(`[departments:merge] id=${freshDepartment.id?.substring(0, 8)}... local.v=${localVersion} server.v=${serverVersion} action=skipped(missing_leader_count)`);
              mergedDepartments.push({
                ...freshDepartment,
                leader_count: prevDepartment.leader_count,
                membersVersion: prevDepartment.membersVersion
              });
            } else {
              // Use server data as fallback
              console.debug(`[departments:merge] id=${freshDepartment.id?.substring(0, 8)}... local.v=${localVersion} server.v=${serverVersion} action=fallback`);
              mergedDepartments.push(freshDepartment);
            }
          });

          this.departments = mergedDepartments;
        }
        this.lastFetch = now;

        console.log('✅ DEPARTMENT SERVICE: Fetched', validDepartments.length, 'departments with location counts in single query');

        this.notify();
        return this.departments;
      }

    } catch (error) {
      console.error("❌ DEPARTMENT SERVICE: Error fetching departments:", error);

      // Fallback to basic departments endpoint if optimized endpoint fails
      try {
        console.log('🔄 DEPARTMENT SERVICE: Falling back to basic departments endpoint');
        const fallbackResponse = await CommanServices.departments();
        if (fallbackResponse.status) {
          const basicDepartments = (fallbackResponse.data || []).filter(dept =>
            dept && dept.id && dept.department_name
          );

          this.departments = basicDepartments.map(dept => ({
            ...dept,
            authoritative_location_count: (dept.associated_locations || []).length
          }));

          this.notify();
          return this.departments;
        }
      } catch (fallbackError) {
        console.error("❌ DEPARTMENT SERVICE: Fallback also failed:", fallbackError);
        this.departments = [];
        this.notify();
        return [];
      }
    }
  },

  // Force refresh (bypasses cache)
  async forceRefresh() {
    this.lastFetch = 0;
    return this._actualFetchDepartments();
  },

  // Get current departments
  getDepartments() {
    return this.departments;
  }
};

// Make service globally available
window.DepartmentService = DepartmentService;

// Helper to count descendants recursively
const countDescendants = (node) => {
  if (!node || !node.children) return 0;
  let count = node.children.length;
  node.children.forEach(child => {
    count += countDescendants(child);
  });
  return count;
};

// Helper to build hierarchy tree from flat list of zones/locations
const buildHierarchy = (zonesList) => {
  if (!Array.isArray(zonesList) || zonesList.length === 0) return [];
  const idMap = new Map();
  
  // Clone and initialize each node with empty children
  zonesList.forEach(zone => {
    idMap.set(zone.id, { ...zone, children: [] });
  });

  const roots = [];
  zonesList.forEach(zone => {
    const node = idMap.get(zone.id);
    if (zone.parent_id && idMap.has(zone.parent_id)) {
      idMap.get(zone.parent_id).children.push(node);
    } else {
      roots.push(node);
    }
  });

  // Sort children at each level deterministically to keep stable order
  const sortChildren = (node) => {
    if (node.children && node.children.length > 0) {
      node.children.sort((a, b) => {
        const aKey = (a.auto_label || a.zone_name || '').toString();
        const bKey = (b.auto_label || b.zone_name || '').toString();
        return aKey.localeCompare(bKey);
      });
      node.children.forEach(sortChildren);
    }
  };

  roots.forEach(sortChildren);

  return roots;
};

// Recursive Location Card Component
const LocationCardNode = ({
  node,
  depth = 0,
  selectedZone,
  selectedLocationIds,
  handleLocationSelect,
  editZone,
  setDeleteModal,
  expandedParentIds,
  toggleExpand,
  getStatusBadgeColor
}) => {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedParentIds.has(node.id);
  const descendantCount = hasChildren ? countDescendants(node) : 0;

  return (
    <div className="space-y-2">
      <Card
        className={`cursor-pointer hover:shadow-md transition-all duration-200 border-2 ${
          selectedZone?.id === node.id
            ? 'border-blue-500 bg-blue-50'
            : selectedLocationIds.has(node.id)
            ? 'border-green-500 bg-green-50'
            : 'border-gray-200 hover:border-gray-300'
        }`}
        onClick={() => editZone(node)}
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1">
              {/* Expand/Collapse Chevron (only if hasChildren) */}
              {hasChildren ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpand(node.id);
                  }}
                  className="p-1 hover:bg-gray-100 rounded text-gray-500 transition-colors flex-shrink-0"
                >
                  <ChevronDown
                    className={`w-5 h-5 transition-transform duration-200 ${
                      isExpanded ? 'transform rotate-180' : ''
                    }`}
                  />
                </button>
              ) : (
                // Spacer if no children to align checkboxes
                <div className="w-7 h-7 flex-shrink-0" />
              )}

              {/* Checkbox for bulk selection */}
              <div onClick={(e) => e.stopPropagation()} className="flex-shrink-0">
                <input
                  type="checkbox"
                  checked={selectedLocationIds.has(node.id)}
                  onChange={() => handleLocationSelect(node.id)}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h3 className="font-semibold text-gray-800 truncate">{node.zone_name}</h3>
                  <IncidentOriginChip description={node.description} />
                </div>

                {/* Show description as subheader if present */}
                {node.description && (
                  <p className="text-sm text-gray-600 mb-2 line-clamp-2">{node.description}</p>
                )}

                <div className="flex items-center gap-2 flex-wrap">
                  {(() => {
                    const g = node.gps || {};
                    const num = (v) => (v === null || v === undefined || v === '' ? NaN : Number(v));
                    const hasGps = (Number.isFinite(num(g.lat)) && Number.isFinite(num(g.lng)))
                      || (Number.isFinite(num(node.gps_latitude)) && Number.isFinite(num(node.gps_longitude)));
                    if (hasGps) return null;
                    return (
                      <Badge
                        data-testid="zone-missing-gps-badge"
                        className="bg-amber-100 text-amber-800 border border-amber-300 text-xs flex items-center gap-1"
                        title="No GPS coordinates — this zone won't plot on the map. Open to set GPS."
                      >
                        <MapPinOff className="h-3 w-3" /> Missing GPS
                      </Badge>
                    );
                  })()}
                  {hasChildren && (
                    <Badge
                      className="bg-indigo-50 text-indigo-700 hover:bg-indigo-100 cursor-pointer transition-colors border border-indigo-200 text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpand(node.id);
                      }}
                    >
                      {descendantCount} {descendantCount === 1 ? 'Sub Location' : 'Sub Locations'}
                    </Badge>
                  )}
                  <Badge className={`text-xs ${
                    typeof node.departmentCount === 'number'
                      ? node.departmentCount === 0
                        ? 'bg-gray-100 text-gray-800'
                        : 'bg-green-100 text-green-800'
                      : node.departments?.length !== undefined
                      ? node.departments.length === 0
                        ? 'bg-gray-100 text-gray-800'
                        : 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {typeof node.departmentCount === 'number'
                      ? `${node.departmentCount} Departments`
                      : node.departments?.length !== undefined
                      ? `${node.departments.length} Departments`
                      : <span className="inline-block w-16 h-3 bg-gray-200 animate-pulse rounded"></span>
                    }
                  </Badge>
                  <Badge className={`text-xs ${
                    typeof node.primaryDepartmentCount === 'number'
                      ? node.primaryDepartmentCount === 0
                        ? 'bg-gray-100 text-gray-800'
                        : 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {typeof node.primaryDepartmentCount === 'number'
                      ? `${node.primaryDepartmentCount} Primary`
                      : <span className="inline-block w-12 h-3 bg-gray-200 animate-pulse rounded"></span>
                    }
                  </Badge>
                  <Badge className={`text-xs ${getStatusBadgeColor(node.status)}`}>
                    {node.status ? node.status.charAt(0).toUpperCase() + node.status.slice(1) : 'Normal'}
                  </Badge>
                </div>
              </div>
            </div>

            <div className="flex gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setDeleteModal({ open: true, id: node.id, name: node.zone_name })}
              >
                <Trash2 className="w-3 h-3 mr-1" />
                Delete
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Render children recursively if expanded */}
      {hasChildren && isExpanded && (
        <div className="ml-5 pl-4 border-l-2 border-gray-200/80 space-y-2 mt-2">
          {node.children.map(child => (
            <LocationCardNode
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedZone={selectedZone}
              selectedLocationIds={selectedLocationIds}
              handleLocationSelect={handleLocationSelect}
              editZone={editZone}
              setDeleteModal={setDeleteModal}
              expandedParentIds={expandedParentIds}
              toggleExpand={toggleExpand}
              getStatusBadgeColor={getStatusBadgeColor}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const LocationsAndZones = () => {
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Resizable panel state
  const [rightPanelWidth, setRightPanelWidth] = useState(() => Math.floor(window.innerWidth * 0.35)); // Default ~35% of screen width

  // Locations & Zones - Department Management State
  const [showDepartmentManagement, setShowDepartmentManagement] = useState(false);
  const [managedLocationId, setManagedLocationId] = useState(null);
  const [associatedDepartments, setAssociatedDepartments] = useState([]);
  const [isLoadingDepartments, setIsLoadingDepartments] = useState(false);
  const [departmentSearchQuery, setDepartmentSearchQuery] = useState("");
  const [isAddingDepartment, setIsAddingDepartment] = useState(false);

  // Bulk selection state for locations
  const [selectedLocationIds, setSelectedLocationIds] = useState(new Set());
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);
  const [suggestionRefreshCounter, setSuggestionRefreshCounter] = useState(0);
  const [attachments, setAttachments] = useState([]);
  const [selectedTreeNodeId, setSelectedTreeNodeId] = useState('');
  const [OriginalLocationHierarchy, setOriginalLocationHierarchy] = useState({});
  const [showUnsavedModal, setShowUnsavedModal] = useState({ toggle: false, data: {} });
  // Delete confirmation modal state (small modal)
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null, name: '' });
  const [isVailid, setIsVailid] = useState(false);


  // Locations & Zones State
  const [formData, setFormData] = useState({
    zone_name: "",
    zone_type: "",
    building: "",
    floor: "",
    corridor: "",
    room: "",
    status: "normal",
    description: "",
    gps_latitude: null,
    gps_longitude: null,
    is_active: true,
    extra_details: [],
    location_hierarchy: {
      location_type: '',
      location_value: '',
      details: [],
      gps: null,
      departments: []
    } // New hierarchical location structure
  });

  const [suggestions, setSuggestions] = useState({
    zoneNames: [],
    zoneTypes: [],
    buildings: [],
    floors: [],
    corridors: [],
    rooms: [],
    departments: []
  });

  // Collapsible section states
  const [isLocationDetailsCollapsed, setIsLocationDetailsCollapsed] = useState(true); // Closed by default
  const [expandedParentIds, setExpandedParentIds] = useState(new Set());

  const toggleExpand = (parentId) => {
    setExpandedParentIds(prev => {
      const next = new Set(prev);
      if (next.has(parentId)) {
        next.delete(parentId);
      } else {
        next.add(parentId);
      }
      return next;
    });
  };

  // Save Guard state - track unfinished rows
  const [hierarchyValidationState, setHierarchyValidationState] = useState({
    hasUnfinishedRows: false,
    unfinishedRowsMessage: null
  });

  // ENHANCED SEARCH: Comprehensive fuzzy search with Fuse.js
  const [searchQuery, setSearchQuery] = useState('');
  const [activeGroup, setActiveGroup] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  console.log("formData", formData)
  // Bulk selection handlers
  const handleLocationSelect = (locationId) => {
    setSelectedLocationIds(prevSelected => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(locationId)) {
        newSelected.delete(locationId);
      } else {
        newSelected.add(locationId);
      }

      // Update Select All checkbox state
      const totalVisibleLocations = displayedZones.length;
      setIsSelectAllChecked(newSelected.size === totalVisibleLocations && totalVisibleLocations > 0);

      return newSelected;
    });
  };

  const handleSelectAll = () => {
    if (isSelectAllChecked) {
      // Deselect all
      setSelectedLocationIds(new Set());
      setIsSelectAllChecked(false);
    } else {
      // Select all visible locations
      const allVisibleIds = new Set(displayedZones.map(zone => zone.id));
      setSelectedLocationIds(allVisibleIds);
      setIsSelectAllChecked(true);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedLocationIds.size === 0) return;

    const confirmMessage = `Are you sure you want to delete ${selectedLocationIds.size} selected location${selectedLocationIds.size > 1 ? 's' : ''}? This action cannot be undone.`;

    if (window.confirm(confirmMessage)) {
      try {
        const deletePromises = Array.from(selectedLocationIds).map(locationId =>
          CommanServices.deleteLocationZones(locationId).then((res) => {
            if (!res.status && res.message) {
              alert(res.message);
            }
          })
        );

        await Promise.all(deletePromises);

        // Clear selection and refresh list and suggestions
        setSelectedLocationIds(new Set());
        setIsSelectAllChecked(false);
        await fetchZones();
        await fetchSuggestions();  // Refresh combobox suggestions
        setSuggestionRefreshCounter(prev => prev + 1);  // Force LocationHierarchyManager to refresh

        console.log(`✅ Successfully deleted ${selectedLocationIds.size} locations`);
      } catch (error) {
        console.error('❌ Error during bulk delete:', error);
        alert('An error occurred while deleting locations. Please try again.');
      }
    }
  };

  // SINGLE SOURCE OF TRUTH: Fetch real-time department counts for a specific location
  const fetchLocationDepartmentCounts = useCallback(async (locationId) => {
    try {
      console.debug('[Card] Fetching real-time counts for location:', locationId);
      const response = await CommanServices.getLocationDepartmentCounts(locationId);
      if (response.status) {
        const departments = response.data || [];

        const totalCount = departments.length;
        const primaryCount = departments.filter(dept => dept.relationship_type === 'primary').length;

        console.debug('[Card] Real-time counts from authoritative source:', { locationId, totalCount, primaryCount, source: 'AUTHORITATIVE_FETCH' });

        // Return the counts instead of updating state directly to avoid dependency issues
        return { totalCount, primaryCount, departments };
      }
    } catch (error) {
      console.error('[Card] Error fetching location department counts:', error);
      return null;
    }
  }, []); // Only depend on api URL which is stable

  // Helper function to update zone counts
  // Helper function to update zone counts
  const updateZoneCounts = useCallback((locationId, totalCount, primaryCount) => {
    setZones(prevZones =>
      prevZones.map(zone => {
        if (zone.id === locationId) {
          return {
            ...zone,
            departmentCount: totalCount,
            primaryDepartmentCount: primaryCount
          };
        }
        return zone;
      })
    );
  }, []);

  // Helper function for debouncing
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  /**
   * Sort zones so the left-side cards match the hierarchical order shown
   * in the Location Tree (depth-first pre-order using parent_id).
   *
   * Algorithm:
   *  - Build a map id -> node (clone to avoid mutating original objects)
   *  - Attach children to their parent if parent_id exists and is present in the map
   *  - Collect root nodes (those without a valid parent)
   *  - Traverse roots in original list order and perform depth-first traversal,
   *    collecting nodes into an ordered flattened array.
   *  - Append any remaining nodes (orphans) in their original order to preserve stability.
   */
  const sortZonesByHierarchy = (zonesList = []) => {
    if (!Array.isArray(zonesList) || zonesList.length === 0) return zonesList;

    // Keep original order array for stable ordering of roots/orphans
    const originalOrder = zonesList.map(z => z.id);

    // Create clone map to safely attach children without mutating original objects
    const nodeMap = new Map(zonesList.map(z => [z.id, { ...z, __children: [] }]));

    // Attach children to parents when possible
    for (const node of nodeMap.values()) {
      if (node.parent_id && nodeMap.has(node.parent_id)) {
        nodeMap.get(node.parent_id).__children.push(node);
      }
    }

    // Roots are nodes that either have no parent_id or their parent is not present
    const roots = [];
    for (const id of originalOrder) {
      const n = nodeMap.get(id);
      if (!n) continue;
      const hasParentInMap = n.parent_id && nodeMap.has(n.parent_id);
      if (!hasParentInMap) roots.push(n);
    }

    const result = [];
    const visited = new Set();

    const visit = (node) => {
      if (!node || visited.has(node.id)) return;
      visited.add(node.id);
      // push original-shaped object (do not include __children in card list)
      const { __children, ...rest } = node;
      result.push(rest);
      // sort children deterministically by label/name to keep stable order across runs
      if (Array.isArray(__children) && __children.length > 0) {
        __children.sort((a, b) => {
          const aKey = (a.auto_label || a.zone_name || '').toString();
          const bKey = (b.auto_label || b.zone_name || '').toString();
          return aKey.localeCompare(bKey);
        });
        __children.forEach(child => visit(child));
      }
    };

    // Visit roots preserving their original order
    for (const root of roots) {
      visit(root);
    }

    // Append any nodes not visited (orphan cycles) preserving original order
    for (const id of originalOrder) {
      if (!visited.has(id) && nodeMap.has(id)) {
        const n = nodeMap.get(id);
        const { __children, ...rest } = n;
        result.push(rest);
        visited.add(id);
      }
    }

    return result;
  };



  // Configure Fuse.js for powerful search
  const fuseOptions = {
    keys: [
      { name: 'auto_label', weight: 2 }, // Primary display name - highest weight
      { name: 'zone_name', weight: 1.8 },
      { name: 'building', weight: 1.5 },
      { name: 'floor', weight: 1.2 },
      { name: 'corridor', weight: 1.2 },
      { name: 'room', weight: 1.2 },
      { name: 'description', weight: 1 },
      { name: 'status', weight: 1.5 },
      { name: 'departments.department_name', weight: 1.3 }, // Search associated departments
      { name: 'extra_details_types', weight: 1.1 }, // Search extra detail types
      { name: 'extra_details_values', weight: 1.1 }, // Search extra detail values
      { name: 'extra_details_combined', weight: 1.0 } // Search combined type:value pairs
    ],
    includeScore: true,
    includeMatches: true,
    threshold: 0.4, // Balance between fuzzy and accurate matching
    distance: 100,
    minMatchCharLength: 1,
    shouldSort: true,
    findAllMatches: true,
    ignoreLocation: true,
    useExtendedSearch: true // Enable status:normal syntax
  };

  // Create Fuse instance with current zones
  const fuse = useMemo(() => {
    const searchableZones = zones.map(zone => {
      const extraDetails = zone.extra_details || [];
      return {
        ...zone,
        // Flatten departments for search
        departments: zone.departments || [],
        // Ensure auto_label is searchable
        auto_label: zone.auto_label || zone.zone_name || '',
        // Flatten extra details for search
        extra_details_types: extraDetails.map(d => d.type).filter(Boolean).join(' '),
        extra_details_values: extraDetails.map(d => d.value).filter(Boolean).join(' '),
        extra_details_combined: extraDetails.filter(d => d.type && d.value).map(d => `${d.type}:${d.value}`).join(' ')
      };
    });
    return new Fuse(searchableZones, fuseOptions);
  }, [zones]);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((query) => {
      if (!query.trim()) {
        setSearchResults([]);
        // Clear selection when showing all zones
        setSelectedLocationIds(new Set());
        setIsSelectAllChecked(false);
        return;
      }

      console.log('🔍 SEARCH: Performing enhanced search for:', query);

      // Handle power syntax (status:normal, building:main, dept:radiology)
      let fuseQuery = query;
      const powerSyntaxMatches = query.match(/(\w+):(\w+)/g);

      if (powerSyntaxMatches) {
        // Convert power syntax to Fuse extended search
        powerSyntaxMatches.forEach(match => {
          const [field, value] = match.split(':');
          if (field === 'status') {
            fuseQuery = fuseQuery.replace(match, `'${value}`); // Exact match for status
          } else if (field === 'building') {
            fuseQuery = fuseQuery.replace(match, `'${value}`);
          } else if (field === 'dept') {
            fuseQuery = fuseQuery.replace(match, `'${value}`);
          } else if (field === 'floor') {
            fuseQuery = fuseQuery.replace(match, `'${value}`);
          }
        });
      }

      const results = fuse.search(fuseQuery);
      console.log('🔍 SEARCH: Found', results.length, 'results');

      setSearchResults(results.map(result => ({
        ...result.item,
        _searchScore: result.score,
        _matches: result.matches
      })));

      // Clear selection when search results change
      setSelectedLocationIds(new Set());
      setIsSelectAllChecked(false);
    }, 250),
    [fuse]
  );

  // Handle search input changes
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    // Clear selection when showing all zones
    setSelectedLocationIds(new Set());
    setIsSelectAllChecked(false);
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Escape clears search
      if (e.key === 'Escape' && searchQuery) {
        clearSearch();
        return;
      }

      // Ctrl/Cmd + K focuses search
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        document.querySelector('#locations-search')?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchQuery]);

  // Get displayed zones (search results or all zones)
  const displayedZones = searchQuery.trim() ? searchResults : zones;

  useEffect(() => {
    // Event listener for location department changes - IMPROVED: Single location refetch
    const handleLocationDepartmentsChanged = async (event) => {
      const { location_id, change, version, source } = event.detail;
      console.log(`🎯 EVENT: Location departments changed, change=${change}, source=${source}, version=${version}:`, { location_id });

      // IMPROVED: Only refetch the affected location instead of all zones
      if (location_id && change !== 'counts_updated') {
        console.debug(`[zones:event] Refetching single location ${location_id.substring(0, 8)}... due to ${change}`);

        try {
          // Fetch fresh data for just this location
          const freshResponse = await CommanServices.getLocationDepartmentCounts(location_id);
          if (freshResponse.status) {
            const freshDepartments = freshResponse.data || [];

            const totalCount = freshDepartments.length;
            const primaryCount = freshDepartments.filter(d => d.relationship_type === 'primary').length;
            const eventVersion = version || Date.now();

            // Update only this location's card
            setZones(prevZones =>
              prevZones.map(zone => {
                if (zone.id === location_id) {
                  const currentVersion = zone.linksVersion || 0;

                  // Only update if event version is newer
                  if (eventVersion >= currentVersion) {
                    console.debug(`[zones:event-update] id=${location_id.substring(0, 8)}... v=${eventVersion} counts={dept:${totalCount}, primary:${primaryCount}}`);
                    return {
                      ...zone,
                      departmentCount: totalCount,
                      primaryDepartmentCount: primaryCount,
                      linksVersion: eventVersion
                    };
                  }
                }
                return zone;
              })
            );
          }

        } catch (error) {
          console.error('❌ Error refetching single location after event:', error);
        }
      }
    };

    document.addEventListener('location.departments.changed', handleLocationDepartmentsChanged);

    return () => {
      document.removeEventListener('location.departments.changed', handleLocationDepartmentsChanged);
    };
  }, []);

  useEffect(() => {
    fetchZones();
    fetchSuggestions();

    // Subscribe to global department updates
    const handleDepartmentUpdate = () => {
      fetchSuggestions(); // This will refresh departments in suggestions
    };

    // CRITICAL: Mirror App Users Config pattern - listen for bidirectional sync events
    const handleDepartmentLocationsChanged = (event) => {
      console.log('🔄 LOCATION SYNC: Department locations changed event received:', event.detail);
      const { department_id, location_id, action } = event.detail;

      // Refresh zones to update department counts on location cards
      fetchZones();

      // If we're currently managing departments for the affected location, refresh immediately
      if (managedLocationId === location_id) {
        console.log('🔄 LOCATION SYNC: Updating Associated Departments panel for current location');

        // Refresh the associated departments list immediately
        setTimeout(async () => {
          try {
            await fetchAssociatedDepartments(location_id);
            console.log('🔄 LOCATION SYNC: Associated Departments panel refreshed successfully');
          } catch (error) {
            console.error('❌ Error refreshing Associated Departments panel:', error);
          }
        }, 500); // Delay to ensure backend has processed the change
      }
    };

    // Simple event listener - refresh when departments are updated
    const handleDepartmentUpdated = (event) => {
      console.log('🔥 SIMPLE: Department updated, refreshing location data');

      fetchZones(); // Refresh location cards

      // If we're managing departments for a location, refresh the list
      if (managedLocationId) {
        console.log('🔥 SIMPLE: Refreshing Manage Associated Departments');
        fetchAssociatedDepartments(managedLocationId);
      }
    };

    // Listen for user department changes (existing listener)
    const handleUserDepartmentChanged = (event) => {
      console.log('🔥 User department changed, refreshing zones:', event.detail);
      fetchZones();
    };

    // Listen for department deletions to refresh zones and update location cards
    const handleDepartmentDeleted = (event) => {
      console.log('🔥 Department deleted, refreshing zones to update location cards:', event.detail);
      fetchZones(); // Refresh zones to update department counts in location cards

      // If we were managing departments for a location, refresh the associated departments
      if (managedLocationId) {
        fetchAssociatedDepartments(managedLocationId);
      }
    };

    // Listen for location deletions to refresh zones and update location cards
    const handleLocationDeleted = (event) => {
      console.log('🔥 Location deleted, refreshing zones to update location cards:', event.detail);
      fetchZones(); // Refresh zones to update location counts in location cards
    };

    DepartmentService.addListener(handleDepartmentUpdate);
    window.addEventListener('departmentUpdated', handleDepartmentUpdate);
    window.addEventListener('department-locations-updated', handleDepartmentUpdated); // Simple event
    window.addEventListener('department.updated', handleDepartmentUpdated); // CRITICAL: Listen for department core data updates

    // CRITICAL: Add new bidirectional sync event listeners (mirror App Users Config pattern)
    window.addEventListener('department.locations.changed', handleDepartmentLocationsChanged);

    window.addEventListener('user.departments.changed', handleUserDepartmentChanged);
    window.addEventListener('department.deleted', handleDepartmentDeleted);
    window.addEventListener('location.deleted', handleLocationDeleted);

    return () => {
      DepartmentService.removeListener(handleDepartmentUpdate);
      window.removeEventListener('departmentUpdated', handleDepartmentUpdate);
      window.removeEventListener('department-locations-updated', handleDepartmentUpdated);
      window.removeEventListener('department.updated', handleDepartmentUpdated); // Clean up department core data listener

      // CRITICAL: Remove new bidirectional sync event listeners
      window.removeEventListener('department.locations.changed', handleDepartmentLocationsChanged);

      window.removeEventListener('user.departments.changed', handleUserDepartmentChanged);
      window.removeEventListener('department.deleted', handleDepartmentDeleted);
      window.removeEventListener('location.deleted', handleLocationDeleted);
    };
  }, []); // FIXED: Removed zones dependency to prevent infinite loop

  // Mouse drag handler for resizable panels
  const handleMouseDown = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = rightPanelWidth;

    const handleMouseMove = (e) => {
      const deltaX = startX - e.clientX;
      const newWidth = Math.max(300, Math.min(800, startWidth + deltaX));
      setRightPanelWidth(newWidth);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const fetchZones = async () => {
    try {
      console.debug('[zones:list] Fetching zones from /api/locations/zones/with-links');
      const response = await CommanServices.locationszoneswithlinks();
      if (response.status) {
        const freshZones = response.data;

        console.debug(`[zones:list] rows=${freshZones.length}, sample=`,
          freshZones.slice(0, 3).map(z => ({
            id: z.id?.substring(0, 8) + '...',
            departmentCount: z.departmentCount,
            primaryDepartmentCount: z.primaryDepartmentCount,
            linksVersion: z.linksVersion
          }))
        );

        // CRITICAL FIX: Non-destructive merge with proper undefined handling
        setZones(prevZones => {
          if (prevZones.length === 0) {
            // Initial load - use all fresh data but ensure order matches hierarchy
            return sortZonesByHierarchy(freshZones);
          }

          // Create maps for efficient lookup
          const prevZonesMap = new Map(prevZones.map(z => [z.id, z]));
          const mergedZones = [];

          freshZones.forEach(freshZone => {
            const prevZone = prevZonesMap.get(freshZone.id);

            if (!prevZone) {
              // New zone - use fresh data
              mergedZones.push(freshZone);
              return;
            }

            // VERSIONED MERGE: Only update if server version is newer AND has valid counts
            const serverVersion = freshZone.linksVersion || 0;
            const localVersion = prevZone.linksVersion || 0;

            // Check if fresh data has DEFINED count fields (not undefined, null, or missing)
            const hasValidCounts = (
              typeof freshZone.departmentCount === 'number' &&
              typeof freshZone.primaryDepartmentCount === 'number'
            );

            if (hasValidCounts && serverVersion >= localVersion) {
              // Server data is newer and has valid counts - use server data
              console.debug(`[zones:merge] id=${freshZone.id?.substring(0, 8)}... local.v=${localVersion} server.v=${serverVersion} action=applied`);
              mergedZones.push(freshZone);
            } else if (!hasValidCounts && (typeof prevZone.departmentCount === 'number' || typeof prevZone.primaryDepartmentCount === 'number')) {
              // Server data is missing counts but local has valid counts - preserve local
              console.debug(`[zones:merge] id=${freshZone.id?.substring(0, 8)}... local.v=${localVersion} server.v=${serverVersion} action=skipped(missing_counts)`);
              mergedZones.push({
                ...freshZone,
                // PRESERVE EXISTING COUNTS - never overwrite with undefined/null
                departmentCount: prevZone.departmentCount,
                primaryDepartmentCount: prevZone.primaryDepartmentCount,
                linksVersion: prevZone.linksVersion
              });
            } else {
              // Use server data (handles case where both are missing counts or server is newer)
              console.debug(`[zones:merge] id=${freshZone.id?.substring(0, 8)}... local.v=${localVersion} server.v=${serverVersion} action=applied(default)`);
              mergedZones.push(freshZone);
            }
          });

          // Ensure the final merged list follows hierarchical order (so left-side cards match the tree)
          return sortZonesByHierarchy(mergedZones);
        });
      }

    } catch (error) {
      console.error("Error fetching zones:", error);
    }
  };

  const fetchSuggestions = async () => {
    try {
      const [zoneNames, zoneTypes, buildings, floors, corridors, rooms] = await Promise.all([
        CommanServices.locationssuggestionszonenames(),
        CommanServices.locationssuggestionszonetypes(),
        CommanServices.locationssuggestionsbuildings(),
        CommanServices.locationssuggestionsfloors(),
        CommanServices.locationssuggestionscorridors(),
        CommanServices.locationssuggestionsrooms()
      ]);
      if (zoneNames.status && zoneTypes.status && buildings.status && floors.status && corridors.status && rooms.status) {
        setSuggestions({
          zoneNames: zoneNames.data,
          zoneTypes: zoneTypes.data,
          buildings: buildings.data,
          floors: floors.data,
          corridors: corridors.data,
          rooms: rooms.data,
          departments: DepartmentService.getDepartments() // Use global department service
        });
      }
    } catch (error) {
      console.error("Error fetching suggestions:", error);
    }
  };

  // Delete functions for suggestions
  const deleteZoneTypeSuggestion = async (zoneType) => {
    console.log('🗑️ deleteZoneTypeSuggestion called with:', zoneType);
    try {
      const response = await CommanServices.deletelocationssuggestionszonetypes(zoneType);
      if (response.status) {
        console.log('🗑️ DELETE response:', response.data);

        // Refresh suggestions to update the dropdown
        console.log('🔄 Refreshing suggestions...');
        await fetchSuggestions();
        console.log('✅ Suggestions refreshed successfully');
      }
    } catch (error) {
      console.error('❌ Delete error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to delete zone type';
      throw new Error(errorMessage);
    }
  };

  const deleteBuildingSuggestion = async (building) => {
    try {
      await CommanServices.deletelocationssuggestionsbuildings(building);

      // Refresh suggestions to update the dropdown
      await fetchSuggestions();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to delete building';
      throw new Error(errorMessage);
    }
  };

  const deleteFloorSuggestion = async (floor) => {
    try {
      await CommanServices.deletelocationssuggestionsfloors(floor);

      // Refresh suggestions to update the dropdown
      await fetchSuggestions();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to delete floor';
      throw new Error(errorMessage);
    }
  };

  const deleteCorridorSuggestion = async (corridor) => {
    try {
      await CommanServices.deletelocationssuggestionscorridors(corridor);

      // Refresh suggestions to update the dropdown
      await fetchSuggestions();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to delete corridor';
      throw new Error(errorMessage);
    }
  };

  const deleteRoomSuggestion = async (room) => {
    try {
      await CommanServices.deletelocationssuggestionsrooms(room);

      // Refresh suggestions to update the dropdown
      await fetchSuggestions();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to delete room';
      throw new Error(errorMessage);
    }
  };



  const handleSave = async () => {
    // Prevent double-clicks and guard against disabled state
    let flag;
    if (isSubmitting || !isFormValid()) {
      console.log("🚫 Save blocked - submitting:", isSubmitting, "valid:", isFormValid());
      flag = false;
      return flag;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    console.log("🚀 Starting hierarchical location save process...");
    console.log("Form data to submit:", formData);
    console.log("Location hierarchy data:", formData.location_hierarchy);

    try {
      // Prepare hierarchical data for submission
      const submitData = {
        location_hierarchy: formData.location_hierarchy,
        description: formData.description?.trim() || ""
      };

      // Determine if this is an edit or create operation
      const isUpdateOperation = isEditing && selectedZone && selectedZone.id;

      let response;

      if (isUpdateOperation) {
        // Stronger safe-update: preserve authoritative server hierarchy and copy only edited fields.
        try {
          const targetId = selectedZone?.id;

          const originalHierarchy = (formData && formData._hierarchy_meta && formData._hierarchy_meta.original_hierarchy)
            ? JSON.parse(JSON.stringify(formData._hierarchy_meta.original_hierarchy))
            : null;

          const modifiedHierarchy = (formData && formData.location_hierarchy) ? formData.location_hierarchy : {};

          const findNodeById = (node, id) => {
            if (!node) return null;
            if (node.id && node.id === id) return node;
            const children = node.details || node.subdetails || [];
            for (const child of children) {
              const found = findNodeById(child, id);
              if (found) return found;
            }
            return null;
          };

          const pick = (obj, keys = []) => {
            for (const k of keys) {
              if (obj && typeof obj[k] !== "undefined") return obj[k];
            }
            return undefined;
          };

          if (originalHierarchy) {
            // Start from authoritative original hierarchy to avoid root/value swaps
            submitData.location_hierarchy = JSON.parse(JSON.stringify(originalHierarchy));

            const sourceNode = findNodeById(modifiedHierarchy, targetId);
            const destNode = findNodeById(submitData.location_hierarchy, targetId);

            if (destNode && sourceNode) {
              // copy type
              const srcType = pick(sourceNode, ['type', 'location_type']);
              if (typeof srcType !== "undefined") {
                if (typeof destNode.type !== "undefined") destNode.type = srcType;
                else destNode.location_type = srcType;
              }

              // copy value
              const srcValue = pick(sourceNode, ['value', 'location_value']);
              if (typeof srcValue !== "undefined") {
                if (typeof destNode.value !== "undefined") destNode.value = srcValue;
                else destNode.location_value = srcValue;
              }

              // copy gps (support both shapes)
              if (typeof sourceNode.gps !== "undefined") {
                destNode.gps = sourceNode.gps;
              } else if (typeof sourceNode.gps_latitude !== "undefined" || typeof sourceNode.gps_longitude !== "undefined") {
                destNode.gps = {
                  lat: sourceNode.gps_latitude,
                  lng: sourceNode.gps_longitude
                };
              }

              // copy departments and their primary state
              if (typeof sourceNode.departments !== "undefined") {
                destNode.departments = sourceNode.departments;
              }
              if (typeof sourceNode.departments_primary !== "undefined") {
                destNode.departments_primary = sourceNode.departments_primary;
              }

              // copy nested children
              if (typeof sourceNode.subdetails !== "undefined") {
                destNode.subdetails = sourceNode.subdetails;
              } else if (typeof sourceNode.details !== "undefined") {
                destNode.details = sourceNode.details;
              }

              // copy status if present on the edited node
              const srcStatus = pick(sourceNode, ['status']);
              if (typeof srcStatus !== 'undefined') {
                destNode.status = srcStatus;
              }

              // copy node-level description if present (some nodes may carry description overrides)
              const srcNodeDescription = pick(sourceNode, ['description']);
              if (typeof srcNodeDescription !== 'undefined') {
                destNode.description = srcNodeDescription;
              }

              // if edited node is the top-level, sync top-level fields
              if (submitData.location_hierarchy && (submitData.location_hierarchy.id === targetId || (destNode && submitData.location_hierarchy.id == destNode.id))) {
                submitData.location_hierarchy.location_type = destNode.type || destNode.location_type || submitData.location_hierarchy.location_type;
                submitData.location_hierarchy.location_value = destNode.value || destNode.location_value || submitData.location_hierarchy.location_value;
                // ensure top-level status and description reflect the edited node where appropriate
                submitData.location_hierarchy.status = destNode.status || submitData.location_hierarchy.status;
                // description is sent separately in submitData.description already, but keep a copy on the hierarchy root if backend expects it
                submitData.location_hierarchy.description = submitData.location_hierarchy.description;
              }
            } else {
              // fallback preserve root
              submitData.location_hierarchy.location_type = originalHierarchy.location_type;
              submitData.location_hierarchy.location_value = originalHierarchy.location_value;
            }
          } else {
            // No authoritative original - defensively preserve root fields
            const currentRootType = submitData.location_hierarchy?.location_type;
            const currentRootValue = submitData.location_hierarchy?.location_value;
            if (selectedZone && selectedZone.zone_name && submitData.location_hierarchy) {
              submitData.location_hierarchy.location_type = submitData.location_hierarchy.location_type || currentRootType;
              submitData.location_hierarchy.location_value = submitData.location_hierarchy.location_value || currentRootValue;
            }
          }
        } catch (e) {
          console.error('❌ Error during robust merge for update operation:', e);
        }

        // Send update to backend using the clicked chip id in the URL (backend will reconcile root)
        console.log('DEBUG: PUT payload for /api/locations/hierarchy/', selectedZone?.id, JSON.parse(JSON.stringify(submitData)));
        console.log("submitData", submitData)
        // Send update as multipart/form-data so media attachments can be included with updates.
        const formDataToSend = new FormData();

        // ── Convert departments from plain IDs to {department_id, relationship_type} objects ──
        // Walk every node in the hierarchy and transform its `departments` array based on `departments_primary`.
        const enrichDepartmentsInHierarchy = (node) => {
          if (!node) return;
          if (Array.isArray(node.departments)) {
            const primarySet = new Set(node.departments_primary || []);
            node.departments = node.departments.map(d => {
              // already an object — update/preserve relationship_type
              if (d && typeof d === 'object' && d.department_id) {
                return { ...d, relationship_type: primarySet.has(d.department_id) ? 'primary' : 'secondary' };
              }
              // plain ID string
              return { department_id: d, relationship_type: primarySet.has(d) ? 'primary' : 'secondary' };
            });
          }
          // recurse into children
          const children = node.details || node.subdetails || [];
          children.forEach(enrichDepartmentsInHierarchy);
        };
        if (submitData.location_hierarchy) {
          enrichDepartmentsInHierarchy(submitData.location_hierarchy);
        }

        formDataToSend.append("location_hierarchy", JSON.stringify(submitData.location_hierarchy));
        formDataToSend.append("description", submitData.description || "");
        // Append any selected attachments from the hierarchy manager
        if (attachments && attachments.length > 0) {
          attachments.forEach((file) => {
            formDataToSend.append("files", file);
          });
        }
        response = await CommanServices.updatelocationshierarchy(formDataToSend, selectedZone.id);
      } else {
        const formDataToSend = new FormData();

        // ── Convert departments from plain IDs to {department_id, relationship_type} objects ──
        const enrichDepartmentsCreate = (node) => {
          if (!node) return;
          if (Array.isArray(node.departments)) {
            const primarySet = new Set(node.departments_primary || []);
            node.departments = node.departments.map(d => {
              if (d && typeof d === 'object' && d.department_id) {
                return { ...d, relationship_type: primarySet.has(d.department_id) ? 'primary' : 'secondary' };
              }
              return { department_id: d, relationship_type: primarySet.has(d) ? 'primary' : 'secondary' };
            });
          }
          (node.details || node.subdetails || []).forEach(enrichDepartmentsCreate);
        };
        if (submitData.location_hierarchy) {
          enrichDepartmentsCreate(submitData.location_hierarchy);
        }

        // Append JSON as string
        formDataToSend.append("location_hierarchy", JSON.stringify(submitData.location_hierarchy));
        formDataToSend.append("description", submitData.description);

        // Append files
        if (attachments && attachments.length > 0) {
          attachments.forEach((file) => {
            formDataToSend.append("files", file);
          });
        }

        response = await CommanServices.setlocationshierarchy(formDataToSend);
      }

      console.log("📥 Response status:", response.status);
      const result = await response.data;
      console.log("📥 Response data:", result);

      if (response.status) {
        console.log(`✅ Location hierarchy ${isUpdateOperation ? 'updated' : 'created'} successfully!`);

        // Show success feedback
        setSubmitError(null);

        // Store original zone info to maintain focus after refresh
        const originalZoneId = selectedZone?.id;
        const originalZoneName = selectedZone?.zone_name;

        // The result is now a list of LocationHierarchyChip objects
        // Refresh the zones list to show all the new location cards
        await fetchZones();
        await fetchSuggestions();

        // CRITICAL FIX: Don't reset form on update, maintain focus on edited hierarchy
        if (isUpdateOperation) {
          console.log('🔥 Maintaining focus on updated hierarchy (preserve selected card):', originalZoneName);
          setAttachments([]);

          // Refresh authoritative hierarchy for the edited chip id and re-bind the form to the same selected card.
          try {
            // Fetch latest hierarchy for the edited chip id so the form shows server state
            const hierarchyResp = await CommanServices.locationshierarchyByid(originalZoneId);
            if (hierarchyResp.status) {
              const latestHierarchyData = await hierarchyResp.data;
              const latestHierarchy = latestHierarchyData.location_hierarchy || {};

              // Initialize departments_primary from relationship_type on each node
              const initDepartmentsPrimary = (node) => {
                if (!node) return;
                if (Array.isArray(node.departments)) {
                  const primaryIds = [];
                  const flatDepts = [];
                  for (const d of node.departments) {
                    if (d && typeof d === 'object') {
                      if (d.relationship_type === 'primary') {
                        primaryIds.push(d.department_id);
                      }
                      flatDepts.push(d.department_id || d.id || '');
                    } else if (typeof d === 'string') {
                      // Case where there is no object information or backend returned plain IDs
                      flatDepts.push(d);
                    }
                  }
                  // Unconditionally initialize so the toggle component has a valid array
                  node.departments_primary = primaryIds;
                  node.departments = flatDepts.filter(Boolean);
                }
                (node.details || node.subdetails || []).forEach(initDepartmentsPrimary);
              };
              initDepartmentsPrimary(latestHierarchy);

              // FALLBACK PATCH (post-save): The freshly-fetched hierarchy may return departments as
              // plain strings (no relationship_type), causing departments_primary to be [].
              // Use the in-memory formData.location_hierarchy which still has the correct
              // departments_primary (set by the user before saving) as the authoritative source.
              const patchPrimaryFromMemory = (freshNode) => {
                if (!freshNode) return;
                if (!freshNode.departments_primary || freshNode.departments_primary.length === 0) {
                  // Look up the same node in the current (pre-overwrite) formData
                  const currentHierarchy = formData?.location_hierarchy;
                  if (currentHierarchy) {
                    const findInCurrent = (n, id) => {
                      if (!n || !id) return null;
                      if (n.id === id) return n;
                      const kids = n.details || n.subdetails || [];
                      for (const k of kids) { const f = findInCurrent(k, id); if (f) return f; }
                      return null;
                    };
                    const matchInCurrent = findInCurrent(currentHierarchy, freshNode.id);
                    if (matchInCurrent && Array.isArray(matchInCurrent.departments_primary) && matchInCurrent.departments_primary.length > 0) {
                      freshNode.departments_primary = matchInCurrent.departments_primary;
                    }
                  }
                }
                (freshNode.details || freshNode.subdetails || []).forEach(patchPrimaryFromMemory);
              };
              patchPrimaryFromMemory(latestHierarchy);


              setFormData({
                location_hierarchy: latestHierarchy,
                description: latestHierarchyData.description || '',
                _hierarchy_meta: {
                  target_chip_id: latestHierarchyData.target_chip_id,
                  root_chip_id: latestHierarchyData.root_chip_id,
                  root_label: latestHierarchyData?.target_chip?.root_label,
                  // store a deep-clone so subsequent edits won't mutate the authoritative copy
                  original_hierarchy: JSON.parse(JSON.stringify(latestHierarchy)),
                  editing_node_id: originalZoneId
                }
              });
              // set original location hierarchy (store a deep-clone to avoid shared references)
              setOriginalLocationHierarchy(JSON.parse(JSON.stringify(latestHierarchy)));
            } else {
              console.warn('⚠️ Could not reload updated hierarchy, keeping existing form state');
            }

            // Also update selectedZone to point to the same clicked card so left-list highlighting remains on that card.
            try {
              const chipResp = await CommanServices.locationszonesChipId(originalZoneId);
              if (chipResp.status) {
                const updatedChip = await chipResp.data;
                // Update only the selected card in the zones list so other cards remain unchanged
                setZones(prevZones => prevZones.map(z => {
                  if (z.id === originalZoneId) {
                    return {
                      ...z,
                      // preserve other fields, but update status & description from server-authoritative chip
                      status: (updatedChip.status !== undefined ? updatedChip.status : z.status),
                      description: (updatedChip.description !== undefined ? updatedChip.description : z.description),
                      // also sync auto_label/zone_name if backend changed them
                      zone_name: updatedChip.zone_name || z.zone_name,
                      auto_label: updatedChip.auto_label || z.auto_label
                    };
                  }
                  return z;
                }));
                setSelectedZone({ id: originalZoneId, zone_name: updatedChip.zone_name || originalZoneName, ...updatedChip });
              } else {
                setSelectedZone(prev => prev || { id: originalZoneId, zone_name: originalZoneName });
              }
            } catch (e) {
              console.warn('⚠️ Could not refresh chip info:', e);
              setSelectedZone(prev => prev || { id: originalZoneId, zone_name: originalZoneName });
            }

            setIsEditing(true);
          } catch (e) {
            console.error('❌ Error while reloading updated hierarchy after save:', e);
            setSelectedZone(prev => prev || { id: originalZoneId, zone_name: originalZoneName });
          }
        } else {
          // For create operations, close the form
          resetForm();
        }

        // CRITICAL FIX: Show accurate card count for updates
        const mainChip = result.find(chip => !chip.parent_id);
        const chipCount = result.length;

        if (isUpdateOperation) {
          // For updates, show that it was updated (don't claim new cards were "generated")
          alert(`✅ Location hierarchy "${mainChip?.auto_label}" updated successfully! Hierarchy contains ${chipCount} location cards.`);
        } else {
          // For creates, show cards generated
          alert(`✅ Location hierarchy "${mainChip?.auto_label}" created successfully! Generated ${chipCount} location cards.`);
        }

      } else {
        setSubmitError(response?.message);
      }
      flag = true
    } catch (error) {
      console.error("💥 Network/Runtime Error:", error);
      setSubmitError(`Network error: ${error.message}`);
      flag = false
    } finally {
      setIsSubmitting(false);
    }
    return flag
  };

  // Handle Enter key submission
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && isFormValid() && !isSubmitting) {
      e.preventDefault();
      handleSave();
    }
  };

  const handleDelete = async (id) => {

    try {
      // Get location details before deletion for cleanup and event emission
      const locationToDelete = zones.find(zone => zone.id === id);
      const locationName = locationToDelete ? locationToDelete.zone_name : 'Unknown';
      const locationDepartments = locationToDelete ? (locationToDelete.departments || []) : [];

      console.log('🔥 Starting location deletion:', locationName, id);
      console.log('🔥 Location has departments:', locationDepartments.length);

      // Delete the location from backend (backend will handle cascading cleanup)
      await CommanServices.deleteLocationZones(id).then((res) => {
        if (res.status) {
          // Refresh zones list
          fetchZones();

          // Reset form if the deleted location was selected
          if (selectedZone && selectedZone.id === id) {
            resetForm();
          }

          // If we were managing departments for this location, close the management panel
          if (managedLocationId === id) {
            setShowDepartmentManagement(false);
            setManagedLocationId(null);
            setAssociatedDepartments([]);
          }

          // Emit events to notify other modules about location deletion
          window.dispatchEvent(new CustomEvent('location.deleted', {
            detail: {
              locationId: id,
              locationName: locationName,
              departmentCount: locationDepartments.length,
              action: 'deleted'
            }
          }));

          console.log('✅ Location deletion completed successfully');
        } else {
          if (res.message) {
            alert(res.message);
          }
        }
      })


    } catch (error) {
      console.error("Error deleting zone:", error);
      if (error.response?.data?.detail) {
        alert(`Cannot delete: ${error.response.data.detail}`);
      } else {
        alert('Error deleting location. Please try again.');
      }
    }
  };

  const resetForm = () => {

    setFormData({
      zone_name: "",
      building: "",
      floor: "",
      corridor: "",
      room: "",
      status: "normal",
      departments: [], // Changed back to 'departments'
      description: "",
      gps_latitude: null,
      gps_longitude: null,
      is_active: true,
      extra_details: []
    });
    setSelectedZone(null);
    setIsEditing(false);
    setShowDepartmentManagement(false); // Close department management panel
    setAttachments([]);
  };

  // Extra Details Helper Functions
  const addExtraDetail = () => {
    if (formData.extra_details.length < 10) {
      setFormData({
        ...formData,
        extra_details: [
          ...formData.extra_details,
          { type: "", value: "" }
        ]
      });
    }
  };

  const removeExtraDetail = (index) => {
    const newExtraDetails = formData.extra_details.filter((_, i) => i !== index);
    setFormData({ ...formData, extra_details: newExtraDetails });
  };

  const updateExtraDetail = (index, field, value) => {
    const newExtraDetails = formData.extra_details.map((detail, i) =>
      i === index ? { ...detail, [field]: value } : detail
    );
    setFormData({ ...formData, extra_details: newExtraDetails });
  };

  const getExtraDetailTypeOptions = () => {
    // Get unique types from existing extra details across all zones
    const existingTypes = zones.flatMap(zone =>
      (zone.extra_details || []).map(detail => detail.type)
    ).filter(Boolean);

    // Add common suggestions
    const commonTypes = [
      "Street", "Address", "Building Number", "Unit", "Suite",
      "Wing", "Section", "Area", "Zone", "District", "Region",
      "Contact", "Phone", "Email", "Website", "Notes"
    ];

    const allTypes = [...new Set([...existingTypes, ...commonTypes])];
    return allTypes.map(type => ({ value: type, label: type }));
  };

  const getExtraDetailValueOptions = (type) => {
    if (!type) return [];

    // Get existing values for this type from all zones
    const existingValues = zones.flatMap(zone =>
      (zone.extra_details || [])
        .filter(detail => detail.type === type)
        .map(detail => detail.value)
    ).filter(Boolean);

    const uniqueValues = [...new Set(existingValues)];
    return uniqueValues.map(value => ({ value, label: value }));
  };

  // Department Management Functions
  const openDepartmentManagement = async (location) => {
    setManagedLocationId(location.id);
    setShowDepartmentManagement(true);
    await fetchAssociatedDepartments(location.id);
  };

  // Function to fetch department count for a specific location - SINGLE SOURCE OF TRUTH
  const fetchLocationDepartmentCount = async (locationId) => {
    try {
      console.log('🔥 Fetching department count for location:', locationId);

      // GET /api/locations/{location_id}/departments (authoritative count)
      const response = await CommanServices.departments();
      if (response.status) {
        const allDepartments = response.data || [];

        // Filter departments that have this location in their associated_locations
        const linkedDepartments = allDepartments.filter(dept => {
          const associatedLocations = dept.associated_locations || [];
          return associatedLocations.includes(locationId);
        });

        console.log('🔥 Location', locationId, 'has', linkedDepartments.length, 'departments');
        return linkedDepartments.length;
      }
    } catch (error) {
      console.error('❌ Error fetching location department count:', error);
      return 0;
    }
  };

  // Function to update location card count in the zones list
  const updateLocationCardCount = async (locationId) => {
    try {
      console.log('🔥 Updating location card count for:', locationId);

      // Get current department count from server (SINGLE SOURCE OF TRUTH)
      const departmentCount = await fetchLocationDepartmentCount(locationId);

      // Update the zones state to reflect the new count
      setZones(prevZones =>
        prevZones.map(zone => {
          if (zone.id === locationId) {
            console.log('🔥 Updating card count for', zone.zone_name, 'from', (zone.departments || []).length, 'to', departmentCount);
            return {
              ...zone,
              // Update the departments count for card display
              departmentCount: departmentCount
            };
          }
          return zone;
        })
      );

    } catch (error) {
      console.error('❌ Error updating location card count:', error);
    }
  };

  // AUTHORITATIVE: Fetch associated departments using the correct api endpoint
  const fetchAssociatedDepartments = async (locationId) => {
    if (!locationId) return;

    setIsLoadingDepartments(true);
    try {
      console.log('🔗 AUTHORITATIVE: Fetching departments for location:', locationId);

      // Use the authoritative endpoint that returns relationship_type
      const response = await CommanServices.getLocationDepartmentCounts(locationId);
      if (response.status) {
        const departmentsWithRelationships = response.data || [];

        console.log('✅ AUTHORITATIVE: Retrieved departments with relationship types:', departmentsWithRelationships);

        setAssociatedDepartments(departmentsWithRelationships);

        // Update location card counts immediately
        updateLocationCardCounts(locationId, departmentsWithRelationships);
      }

    } catch (error) {
      console.error('❌ Error fetching associated departments:', error);
      setAssociatedDepartments([]);
    } finally {
      setIsLoadingDepartments(false);
    }
  };

  // Update location card counts and emit events (VERSIONED, NON-DESTRUCTIVE)
  const updateLocationCardCounts = (locationId, departments) => {
    const totalCount = departments.length;
    const primaryCount = departments.filter(dept => dept.relationship_type === 'primary').length;

    // Generate version for this update
    const updateVersion = Date.now();

    console.log('🔥 Updating location card counts:', { locationId, totalCount, primaryCount, version: updateVersion });

    // CRITICAL FIX: Versioned, non-destructive update
    setZones(prevZones =>
      prevZones.map(zone => {
        if (zone.id === locationId) {
          const currentVersion = zone.linksVersion || 0;

          // Always update if we have authoritative department data
          console.debug(`[zones:update] id=${locationId.substring(0, 8)}... current.v=${currentVersion} update.v=${updateVersion} counts={dept:${totalCount}, primary:${primaryCount}}`);

          return {
            ...zone,
            departmentCount: totalCount,
            primaryDepartmentCount: primaryCount,
            linksVersion: updateVersion
          };
        }
        return zone;
      })
    );

    // Emit event for cross-module consistency
    const event = new CustomEvent('location.departments.changed', {
      detail: {
        location_id: locationId,
        change: 'counts_updated',
        total_departments: totalCount,
        primary_departments: primaryCount,
        version: updateVersion,
        source: 'UPDATE_COUNTS'
      }
    });
    document.dispatchEvent(event);
  };

  const addDepartmentToLocation = async (departmentId, departmentName) => {
    if (!managedLocationId) return;

    try {
      setIsAddingDepartment(true);
      console.log('🔗 JOIN TABLE: Adding department to location using authoritative endpoint:', departmentName, 'to', managedLocationId);

      // CRITICAL FIX: Use JOIN TABLE endpoint POST /api/departments/{dept_id}/locations/{loc_id}
      const response = await CommanServices.addDepartmentToLocation(departmentId, managedLocationId);

      // FIXED: Skip optimistic updates to prevent Primary count reset
      // Instead, directly fetch authoritative data to preserve existing Primary relationships
      console.log('🔗 JOIN TABLE: Fetching fresh data to preserve Primary relationships');

      // Use single source of truth approach - fetch fresh data immediately
      const freshResponse = await CommanServices.getLocationDepartmentCounts(managedLocationId);
      if (freshResponse.status) {
        const freshDepartments = freshResponse.data || [];

        console.log('✅ AUTHORITATIVE: Fresh departments after add:', freshDepartments);

        // Update panel with authoritative data (preserves Primary relationships)
        setAssociatedDepartments(freshDepartments);

        // CRITICAL: Update ONLY this location card with versioned approach
        const totalCount = freshDepartments.length;
        const primaryCount = freshDepartments.filter(d => d.relationship_type === 'primary').length;
        const updateVersion = Date.now();

        console.debug(`[zones:add] Updating single card for location ${managedLocationId.substring(0, 8)}... dept:${totalCount} primary:${primaryCount} v:${updateVersion}`);

        // Update only the affected location card (DON'T TOUCH OTHER CARDS)
        setZones(prevZones =>
          prevZones.map(zone => {
            if (zone.id === managedLocationId) {
              return {
                ...zone,
                departmentCount: totalCount,
                primaryDepartmentCount: primaryCount,
                linksVersion: updateVersion
              };
            }
            return zone; // Other cards unchanged
          })
        );

        // Emit event for consistency
        const event = new CustomEvent('location.departments.changed', {
          detail: {
            location_id: managedLocationId,
            department_id: departmentId,
            change: 'add',
            total_departments: totalCount,
            primary_departments: primaryCount,
            version: updateVersion,
            source: 'ADD_DEPARTMENT'
          }
        });
        document.dispatchEvent(event);

        console.log(`[event] location.departments.changed {location_id: ${managedLocationId.substring(0, 8)}..., change: add}`);

        console.log('✅ JOIN TABLE: Department link completed successfully');
        setDepartmentSearchQuery(""); // Clear search
        alert(`✅ ${departmentName} linked successfully using JOIN TABLE!`);
      }

    } catch (error) {
      console.error('❌ JOIN TABLE: Error linking department:', error);
      alert('Error linking department. Please try again.');

      // Revert by fetching current state
      try {
        await fetchAssociatedDepartments(managedLocationId);
      } catch (fetchError) {
        console.error('❌ Error reverting after failed add:', fetchError);
      }
    } finally {
      setIsAddingDepartment(false);
    }
  };

  const removeDepartmentFromLocation = async (departmentId, departmentName) => {
    console.log('🔓 JOIN TABLE: Starting removal process');
    console.log('🔓 Department ID:', departmentId);
    console.log('🔓 Department Name:', departmentName);
    console.log('🔓 Managed Location ID:', managedLocationId);

    if (!managedLocationId) {
      console.error('❌ ERROR: managedLocationId is null/undefined!');
      alert('❌ Error: No location selected for department removal. Please refresh and try again.');
      return;
    }

    try {
      console.log('🔓 JOIN TABLE: Removing department from location using authoritative endpoint:', departmentName, 'from', managedLocationId);

      // Optimistic UI update - immediately remove from the list for faster feedback
      const optimisticDepartments = associatedDepartments.filter(dept => dept.id !== departmentId);
      setAssociatedDepartments(optimisticDepartments);

      // CRITICAL FIX: Use JOIN TABLE endpoint DELETE /api/departments/{dept_id}/locations/{loc_id}
      const response = await CommanServices.deleteDepartmentToLocation(departmentId, managedLocationId);

      // Refresh authoritative data
      console.log('🔓 JOIN TABLE: Refreshing authoritative data after unlink');
      await fetchAssociatedDepartments(managedLocationId);
      fetchZones(); // Update location card counts

      console.log('✅ JOIN TABLE: Department unlink completed successfully');
      alert(`✅ ${departmentName} removed successfully using JOIN TABLE!`);

    } catch (error) {
      console.error('❌ JOIN TABLE: Error unlinking department:', error);
      console.error('❌ Error details:', error.response?.data || error.message);
      alert('Error removing department. Please try again.');

      // Revert optimistic updates on error
      console.log('🔓 ERROR: Reverting optimistic updates...');
      await fetchAssociatedDepartments(managedLocationId);
      fetchZones();
    }
  };

  const updateDepartmentContact = async (departmentId, field, value) => {
    try {
      console.log('🔥 Updating department contact:', departmentId, field, value);

      // Fetch current department data
      const deptResponse = await CommanServices.getbyIddepartments(departmentId);
      if (deptResponse.status) {
        const department = deptResponse.data;

        // Update the specific field
        const updatedDepartment = {
          ...department,
          [field]: value
        };

        await CommanServices.updatedepartments(departmentId, updatedDepartment);

        // Refresh the associated departments list to show updated data
        await fetchAssociatedDepartments(managedLocationId);

        console.log('✅ Successfully updated department contact');
      }
    } catch (error) {
      console.error('❌ Error updating department contact:', error);
      alert('Error updating contact information. Please try again.');
    }
  };

  const copyZone = async (zone) => {
    try {
      console.log('📋 Copying location:', zone.zone_name);

      // Create a copy with modified name and new ID
      const copiedZone = {
        ...zone,
        zone_name: `${zone.zone_name} (Copy)`,
        description: zone.description ? `${zone.description} (Copy)` : ''
      };

      // Remove the original ID so a new one is generated
      delete copiedZone.id;

      // Set the copied zone as the current form data for editing
      setFormData({
        zone_name: copiedZone.zone_name,
        building: copiedZone.building || '',
        floor: copiedZone.floor || '',
        corridor: copiedZone.corridor || '',
        room: copiedZone.room || '',
        description: copiedZone.description || '',
        status: copiedZone.status || 'normal',
        departments: copiedZone.departments || []
      });

      // Set editing mode
      setSelectedZone(copiedZone);
      setIsEditing(false); // This will be a new zone, not editing existing

      console.log('✅ Location copied and ready for editing');
      alert(`Location "${zone.zone_name}" copied! You can now modify the details and save as a new location.`);

    } catch (error) {
      console.error('❌ Error copying location:', error);
      alert('Failed to copy location. Please try again.');
    }
  };

  const editZone = async (zone) => {
    try {
      if ((OriginalLocationHierarchy && Object.keys(OriginalLocationHierarchy).length > 0) && (formData && formData.location_hierarchy && Object.keys(formData.location_hierarchy).length > 0)) {
        console.log("+++++++++++++OriginalLocationHierarchy", OriginalLocationHierarchy)
        console.log("+++++++++++++formData.location_hierarchy", formData.location_hierarchy)
        const isChanges = isDifferent(OriginalLocationHierarchy, formData.location_hierarchy);
        if (isChanges || attachments.length > 0) {
          setShowUnsavedModal({ toggle: true, data: zone });
        } else {
          editZoneLocation(zone);
        }
      } else {
        editZoneLocation(zone);
      }
    } catch (error) {
      console.error(error)
    }
  }

  const editZoneLocation = async (zone) => {
    try {

      setAttachments([]);
      // Close Associated Departments panel if it's open
      if (showDepartmentManagement) {
        setShowDepartmentManagement(false);
      }

      // Check if this zone has hierarchical data (has parent_id, hierarchy_path, or root_id)
      // OR try hierarchical loading for all zones as fallback detection
      const isHierarchical = !!(zone.parent_id || zone.hierarchy_path || zone.root_id);


      if (isHierarchical) {
        // This is a hierarchical location - load the full hierarchy using ANY chip ID
        const response = await CommanServices.locationshierarchyByid(zone.id);

        if (response.status) {
          const hierarchyData = await response.data;


          // Store the enhanced hierarchy information
          // We need the edit form to bind to the exact selected chip (the clicked card),
          // not accidentally to the parent/root. Build a defensive selection strategy:
          // 1) Prefer the node that matches the clicked card id (zone.id).
          // 2) Fallback to backend-provided target_chip or a tree search for robustness.
          const hierarchy = hierarchyData.location_hierarchy || {};

          // Populate departments_primary from saved relationship_type so the primary toggle is restored
          const restorePrimaryFromDepts = (node) => {
            if (!node) return;
            if (Array.isArray(node.departments)) {
              const primaryIds = [];
              const flatDepts = [];
              for (const d of node.departments) {
                if (d && typeof d === 'object') {
                  if (d.relationship_type === 'primary') {
                    primaryIds.push(d.department_id);
                  }
                  flatDepts.push(d.department_id || d.id || '');
                } else if (typeof d === 'string') {
                  flatDepts.push(d);
                }
              }
              // Unconditionally initialize so the toggle component has a valid array
              node.departments_primary = primaryIds;
              node.departments = flatDepts.filter(Boolean);
            }
            if (Array.isArray(node.details)) node.details.forEach(restorePrimaryFromDepts);
            if (Array.isArray(node.subdetails)) node.subdetails.forEach(restorePrimaryFromDepts);
          };
          restorePrimaryFromDepts(hierarchy);

          // FALLBACK PATCH: If the clicked zone's node still has empty departments_primary
          // (because the hierarchy API returned departments as plain strings without relationship_type),
          // use the flat zone.departments list (which always has relationship_type) as the source of truth.
          if (Array.isArray(zone.departments) && zone.departments.length > 0) {
            const hasPrimaryInfoInZone = zone.departments.some(d => d && typeof d === 'object' && d.relationship_type);
            if (hasPrimaryInfoInZone) {
              // Build a map of department_id → relationship_type from the flat zone data
              const zonePrimarySet = new Set(
                zone.departments
                  .filter(d => d && typeof d === 'object' && d.relationship_type === 'primary')
                  .map(d => d.department_id)
              );

              // Find and patch the target node (either root or clicked chip)
              const patchNode = (node, targetId) => {
                if (!node) return;
                if (node.id && node.id === targetId) {
                  // Only patch if departments_primary is empty but node has departments
                  if ((!node.departments_primary || node.departments_primary.length === 0) && Array.isArray(node.departments) && node.departments.length > 0) {
                    node.departments_primary = node.departments.filter(d => zonePrimarySet.has(typeof d === 'object' ? d.department_id : d));
                  }
                  return;
                }
                const children = node.details || [];
                children.forEach(child => patchNode(child, targetId));
                if (Array.isArray(node.subdetails)) node.subdetails.forEach(child => patchNode(child, targetId));
              };
              patchNode(hierarchy, zone.id);
            }
          }


          // Recursive search for the target node inside the hierarchy (safe & defensive)
          const findTargetNode = (node, targetId) => {
            if (!node || !targetId) return null;
            if (node.id && node.id === targetId) return node;
            const details = node.details || [];
            for (const d of details) {
              const stack = [d];
              while (stack.length) {
                const cur = stack.pop();
                if (!cur) continue;
                if (cur.id && cur.id === targetId) return cur;
                if (cur.subdetails && cur.subdetails.length) stack.push(...cur.subdetails);
                if (cur.details && cur.details.length) stack.push(...cur.details);
              }
            }
            return null;
          };

          // Clicked chip id (explicit) and backend-provided ids
          const clickedChipId = zone.id;
          const backendTargetId = hierarchyData.target_chip_id;

          // Try to locate the node in order of preference:
          // 1) backend-provided target_chip payload (if backend knows it)
          // 2) direct search for the clicked chip id (most reliable for left-list clicks)
          // 3) fallback to searching backendTargetId if available
          const targetNode =
            hierarchyData.target_chip ||
            findTargetNode(hierarchy, clickedChipId) ||
            findTargetNode(hierarchy, backendTargetId);

          // Determine which id we'll treat as the selected chip (prefer clicked id for UI selection)
          const effectiveSelectedId = clickedChipId || backendTargetId || (targetNode && targetNode.id);

          // Build a modified hierarchy where the edit-form top-level represents the selected node.
          // This ensures the Edit panel binds to the clicked child node (type, value, gps, departments, details).
          // We still preserve the original full hierarchy in _hierarchy_meta.original_hierarchy for safe merge on save.
          // Keep the full server-sent hierarchy as-is in the form state. Do NOT strip the selected node out of the tree.
          // Store the clicked chip's id as the editing target so the hierarchy editor can bind edits to that node.
          setFormData({
            location_hierarchy: hierarchy, // full authoritative hierarchy (unchanged)
            description: hierarchyData.description || hierarchy.description || "",
            _hierarchy_meta: {
              target_chip_id: hierarchyData.target_chip_id,
              root_chip_id: hierarchyData.root_chip_id,
              root_label: hierarchyData?.target_chip?.root_label,
              // preserve a deep-clone of authoritative original for safe merges on save
              original_hierarchy: JSON.parse(JSON.stringify(hierarchy)),
              editing_node_id: clickedChipId // explicitly mark which node is being edited
            }
          });

          // set original location hierarchy (store deep-clone to prevent accidental mutation)
          setOriginalLocationHierarchy(JSON.parse(JSON.stringify(hierarchy)));

          // Ensure selectedZone references the clicked card id so UI highlighting and save target the exact node
          const selectedZoneName = (targetNode && (targetNode.value || targetNode.location_value)) || zone.zone_name || hierarchyData.root_label;
          setSelectedZone({
            id: clickedChipId,
            zone_name: selectedZoneName,
            ...zone
          });
          setIsEditing(true);
          setHierarchyValidationState({ ...hierarchyValidationState, hasUnfinishedRows: false })
          return; // Exit early on success
        } else {
          console.error('❌ Failed to load hierarchy data for hierarchical zone');
          // Fallback to regular zone editing
          await editZoneAsRegular(zone);
          return;
        }
      } else {
        // Try hierarchical loading first as fallback detection, then regular if it fails
        console.log('🔥 Attempting hierarchical loading as fallback detection for zone:', zone.zone_name);
        const response = await CommanServices.locationshierarchyByid(zone.id);

        if (response.status) {
          console.log('🔥 Zone is actually hierarchical (detected via api)!');
          const hierarchyData = await response.data;

          // Store the enhanced hierarchy information (detected via API)
          const hierarchy = hierarchyData.location_hierarchy || {};

          // Populate departments_primary from saved relationship_type so the primary toggle is restored
          const restorePrimaryFromDepts2 = (node) => {
            if (!node) return;
            if (Array.isArray(node.departments)) {
              const primaryIds = [];
              const flatDepts = [];
              for (const d of node.departments) {
                if (d && typeof d === 'object') {
                  if (d.relationship_type === 'primary') {
                    primaryIds.push(d.department_id);
                  }
                  flatDepts.push(d.department_id || d.id || '');
                } else if (typeof d === 'string') {
                  flatDepts.push(d);
                }
              }
              // Unconditionally initialize so the toggle component has a valid array
              node.departments_primary = primaryIds;
              node.departments = flatDepts.filter(Boolean);
            }
            if (Array.isArray(node.details)) node.details.forEach(restorePrimaryFromDepts2);
            if (Array.isArray(node.subdetails)) node.subdetails.forEach(restorePrimaryFromDepts2);
          };
          restorePrimaryFromDepts2(hierarchy);

          // FALLBACK PATCH: Same as path 1 — use zone.departments flat list as source of truth
          // if the hierarchy API returned departments as plain strings without relationship_type.
          if (Array.isArray(zone.departments) && zone.departments.length > 0) {
            const hasPrimaryInfoInZone2 = zone.departments.some(d => d && typeof d === 'object' && d.relationship_type);
            if (hasPrimaryInfoInZone2) {
              const zonePrimarySet2 = new Set(
                zone.departments
                  .filter(d => d && typeof d === 'object' && d.relationship_type === 'primary')
                  .map(d => d.department_id)
                  
              );
              const patchNode2 = (node, targetId) => {
                if (!node) return;
                if (node.id && node.id === targetId) {
                  if ((!node.departments_primary || node.departments_primary.length === 0) && Array.isArray(node.departments) && node.departments.length > 0) {
                    node.departments_primary = node.departments.filter(d => zonePrimarySet2.has(typeof d === 'object' ? d.department_id : d));
                  }
                  return;
                }
                (node.details || []).forEach(child => patchNode2(child, targetId));
                if (Array.isArray(node.subdetails)) node.subdetails.forEach(child => patchNode2(child, targetId));
              };
              patchNode2(hierarchy, zone.id);
            }
          }

          // Reuse the same recursive helper as above to find the target chip in the returned hierarchy
          const findTargetNode2 = (node, targetId) => {
            if (!node) return null;
            if (node.id && node.id === targetId) return node;
            const details = node.details || [];
            for (const d of details) {
              const stack = [d];
              while (stack.length) {
                const cur = stack.pop();
                if (!cur) continue;
                if (cur.id && cur.id === targetId) return cur;
                if (cur.subdetails && cur.subdetails.length) stack.push(...cur.subdetails);
                if (cur.details && cur.details.length) stack.push(...cur.details);
              }
            }
            return null;
          };

          const targetId2 = hierarchyData.target_chip_id;
          // Prefer backend-provided target_chip payload when available
          const targetNode2 = hierarchyData.target_chip || findTargetNode2(hierarchy, targetId2);

          const modifiedHierarchy2 = {
            ...hierarchy,
            location_type: (targetNode2 && (targetNode2.type || targetNode2.location_type)) || hierarchy.location_type,
            location_value: (targetNode2 && (targetNode2.value || targetNode2.location_value)) || hierarchy.location_value,
          };

          setFormData({
            location_hierarchy: modifiedHierarchy2,
            description: hierarchyData.description || "",
            _hierarchy_meta: {
              target_chip_id: hierarchyData.target_chip_id,
              root_chip_id: hierarchyData.root_chip_id,
              root_label: hierarchyData.root_label,
              // preserve a deep-clone of the original server hierarchy
              original_hierarchy: JSON.parse(JSON.stringify(hierarchy)),
              editing_node_id: targetId2 || hierarchyData.target_chip_id // mark the node backend identifies (or targetId2) as editing target
            }
          });

          // store a deep-clone so the OriginalLocationHierarchy state is not mutated by edits
          setOriginalLocationHierarchy(JSON.parse(JSON.stringify(modifiedHierarchy2)));

          // Ensure selectedZone references the target chip so left-list highlighting and saves operate on the exact node
          const selectedZoneName2 = (targetNode2 && (targetNode2.value || targetNode2.location_value)) || zone.zone_name || hierarchyData.root_label;
          setSelectedZone({
            id: targetId2 || hierarchyData.root_chip_id,
            zone_name: selectedZoneName2,
            ...zone
          });
          setIsEditing(true);
          return; // Exit early on success
        } else {
          console.log('🔥 Zone is not hierarchical, using regular editing');
          // This zone is not hierarchical - use regular editing
          await editZoneAsRegular(zone);
        }
      }
    } catch (error) {
      console.error('❌ Error loading location for editing:', error);
      // Fallback to regular zone editing
      await editZoneAsRegular(zone);
    }
  };

  // Helper function to find the main chip ID for a hierarchical location
  const findMainChipId = async (chipId) => {
    try {
      const response = await CommanServices.locationszonesChipId(chipId);
      if (response.status) {
        const chipData = await response.data;
        if (chipData.parent_id) {
          // This chip has a parent, find the root
          return await findMainChipId(chipData.parent_id);
        } else {
          // This is the root chip
          return chipData.id;
        }
      }
    } catch (error) {
      console.error('❌ Error finding main chip:', error);
    }
    return chipId; // Fallback
  };

  // Fallback function for regular zone editing
  const editZoneAsRegular = async (zone) => {
    const response = await CommanServices.locationszonesChipId(zone.id);
    if (response.status) {
      const latestZoneData = await response.data;

      // Convert regular zone to hierarchical format
      const hierarchyData = {
        location_type: latestZoneData.zone_type || 'Location',
        location_value: latestZoneData.zone_name,
        details: [],
        gps: latestZoneData.gps_latitude ? {
          lat: latestZoneData.gps_latitude,
          lng: latestZoneData.gps_longitude
        } : null,
        departments: latestZoneData.departments?.map(d => d.department_id) || [],
        // Initialize primary dept IDs from existing relationship_type
        departments_primary: latestZoneData.departments
          ?.filter(d => d.relationship_type === 'primary')
          ?.map(d => d.department_id) || []
      };

      setFormData({
        location_hierarchy: hierarchyData,
        description: latestZoneData.description || ""
      });
      setSelectedZone(latestZoneData);
      setIsEditing(true);
    }
  };

  // Helper function for department field changes with live sync - MIRRORS App Users Config pattern
  const handleDepartmentChange = async (selectedDepartments) => {
    const oldDepartments = formData.departments || [];
    const oldDepartmentIds = oldDepartments.map(dept => dept.department_id || dept);

    // Convert department IDs to DepartmentAssociation objects that backend expects
    const departmentAssociations = selectedDepartments.map(deptId => {
      // Find department name from suggestions
      const dept = suggestions.departments.find(d => d.id === deptId);
      return {
        department_id: deptId,
        department_name: dept ? dept.department_name : 'Unknown',
        relationship_type: 'primary' // Default to primary since we removed Primary/Secondary UI
      };
    });

    // Always update the UI state first - this ensures the checkboxes work immediately - MIRRORS App Users Config
    setFormData({ ...formData, departments: departmentAssociations });

    // Only do live sync if we're editing an existing zone
    if (isEditing && selectedZone) {
      try {
        console.log('🔥 Department changes for location:', selectedZone.zone_name);
        console.log('🔥 Old department IDs:', oldDepartmentIds);
        console.log('🔥 New department IDs:', selectedDepartments);

        // Find added and removed departments for event emission - MIRRORS Department → Location pattern
        const addedDepartments = selectedDepartments.filter(deptId => !oldDepartmentIds.includes(deptId));
        const removedDepartments = oldDepartmentIds.filter(deptId => !selectedDepartments.includes(deptId));

        console.log('🔥 Added departments:', addedDepartments);
        console.log('🔥 Removed departments:', removedDepartments);

        // Update the location in backend (backend handles bidirectional sync)
        const updatedZone = {
          ...selectedZone,
          departments: departmentAssociations
        };

        await CommanServices.updatelocationszones(selectedZone.id, updatedZone);

        // Emit events for each added department - CRITICAL: This mirrors the user.departments.changed pattern
        for (const departmentId of addedDepartments) {
          const dept = suggestions.departments.find(d => d.id === departmentId);
          console.log('🔥 Emitting location.departments.changed event for added department:', { location_id: selectedZone.id, department_id: departmentId, action: 'added' });
          window.dispatchEvent(new CustomEvent('location.departments.changed', {
            detail: {
              location_id: selectedZone.id,
              location_name: selectedZone.zone_name,
              department_id: departmentId,
              department_name: dept ? dept.department_name : 'Unknown',
              action: 'added'
            }
          }));
        }

        // Emit events for each removed department
        for (const departmentId of removedDepartments) {
          const dept = suggestions.departments.find(d => d.id === departmentId);
          console.log('🔥 Emitting location.departments.changed event for removed department:', { location_id: selectedZone.id, department_id: departmentId, action: 'removed' });
          window.dispatchEvent(new CustomEvent('location.departments.changed', {
            detail: {
              location_id: selectedZone.id,
              location_name: selectedZone.zone_name,
              department_id: departmentId,
              department_name: dept ? dept.department_name : 'Unknown',
              action: 'removed'
            }
          }));
        }

        // Refresh zones to update counts
        fetchZones();

        console.log('✅ Location department update completed successfully (bidirectional sync handled by backend + events)');
      } catch (error) {
        console.error('❌ Error updating location departments:', error);
        // Revert UI on error - same as App Users Config pattern
        setFormData({ ...formData, departments: oldDepartments });
        alert('Error saving department associations. Please try again.');
      }
    }
  };

  // Add location to department's associated locations (bidirectional sync)
  const addLocationToDepartment = async (location, departmentId) => {
    try {
      console.log('🔥 Starting addLocationToDepartment process');
      console.log('🔥 Location to add:', location.zone_name, location.id);
      console.log('🔥 Department ID:', departmentId);

      // Fetch current department data
      const deptResponse = await CommanServices.getbyIddepartments(departmentId);
      if (deptResponse.status) {
        const department = deptResponse.data;

        console.log('🔥 Current department data:', department);

        // Check current location assignments and avoid duplicates
        const currentLocations = department.associated_locations || [];
        const locationExists = currentLocations.some(locId =>
          locId === location.id || locId === location.zone_name
        );

        if (locationExists) {
          console.log('🔥 Location already associated with department, skipping');
          return;
        }

        // Add location to department's associated locations
        const updatedLocations = [...currentLocations, location.id];
        const updatedDepartment = {
          ...department,
          associated_locations: updatedLocations
        };

        console.log('🔥 Updating department with new location');
        await CommanServices.updatedepartments(departmentId, updatedDepartment);

        // Emit events for real-time updates in Departments & Units
        window.dispatchEvent(new CustomEvent('department.locations.changed', {
          detail: {
            department_id: departmentId,
            department_name: department.department_name,
            location_id: location.id,
            location_name: location.zone_name,
            action: 'added'
          }
        }));

        console.log('✅ Successfully added location to department');
      }

    } catch (error) {
      console.error('❌ Error adding location to department:', error);
      throw error;
    }
  };

  // Remove location from department's associated locations (bidirectional sync)
  const removeLocationFromDepartment = async (location, departmentId) => {
    try {
      console.log('🔥 Starting removeLocationFromDepartment process');
      console.log('🔥 Location to remove:', location.zone_name, location.id);
      console.log('🔥 Department ID:', departmentId);

      // Fetch current department data
      const deptResponse = await CommanServices.getbyIddepartments(departmentId);
      if (deptResponse.status) {
        const department = deptResponse.data;

        console.log('🔥 Current department data:', department);

        // Remove location from department's associated locations (handle both ID and name)
        const currentLocations = department.associated_locations || [];
        const updatedLocations = currentLocations.filter(locId =>
          locId !== location.id && locId !== location.zone_name
        );

        const updatedDepartment = {
          ...department,
          associated_locations: updatedLocations
        };

        console.log('🔥 Updating department with removed location');
        await CommanServices.updatedepartments(departmentId, updatedDepartment);

        // Emit events for real-time updates in Departments & Units
        window.dispatchEvent(new CustomEvent('department.locations.changed', {
          detail: {
            department_id: departmentId,
            department_name: department.department_name,
            location_id: location.id,
            location_name: location.zone_name,
            action: 'removed'
          }
        }));

        console.log('✅ Successfully removed location from department');
      }

    } catch (error) {
      console.error('❌ Error removing location from department:', error);
      throw error;
    }
  };

  // Building, Floor, Room handlers (zone_type removed)

  const handleBuildingChange = (value) => {
    setFormData({ ...formData, building: value, floor: "", corridor: "", room: "" }); // Reset dependent fields
  };

  // Save building to suggestions (called on Enter/blur)
  const saveBuildingToSuggestions = async (value) => {
    if (value && value.trim() && !suggestions.buildings.includes(value.trim())) {
      const trimmedValue = value.trim();
      // Refresh suggestions from backend to ensure they include any new buildings created by zones
      await fetchSuggestions();
    }
  };

  const handleFloorChange = (value) => {
    setFormData({ ...formData, floor: value, corridor: "", room: "" }); // Reset corridor and room when floor changes
  };

  // Save floor to suggestions (called on Enter/blur)
  const saveFloorToSuggestions = async (value) => {
    if (value && value.trim() && !suggestions.floors.includes(value.trim())) {
      const trimmedValue = value.trim();
      // Refresh suggestions from backend to ensure they include any new floors created by zones
      await fetchSuggestions();
    }
  };

  const handleCorridorChange = (value) => {
    setFormData({ ...formData, corridor: value, room: "" }); // Reset room when corridor changes
  };

  // Save corridor to suggestions (called on Enter/blur)
  const saveCorridorToSuggestions = async (value) => {
    if (value && value.trim() && !suggestions.corridors.includes(value.trim())) {
      const trimmedValue = value.trim();
      // Refresh suggestions from backend to ensure they include any new corridors created by zones
      await fetchSuggestions();
    }
  };

  const handleRoomChange = (value) => {
    setFormData({ ...formData, room: value });
  };

  // Save room to suggestions (called on Enter/blur)
  const saveRoomToSuggestions = async (value) => {
    if (value && value.trim() && !suggestions.rooms.includes(value.trim())) {
      const trimmedValue = value.trim();
      // Refresh suggestions from backend to ensure they include any new rooms created by zones
      await fetchSuggestions();
    }
  };

  // Generate display label with extra details
  const generateDisplayLabel = (zone) => {
    let label = zone.zone_name || '';

    // Add building, floor, corridor, room
    if (zone.building) label += ` – ${zone.building}`;
    if (zone.floor) label += ` – ${zone.floor}`;
    if (zone.corridor) label += ` – ${zone.corridor}`;
    if (zone.room) label += ` – ${zone.room}`;

    // Add extra details in bracketed format
    const extraDetails = zone.extra_details || [];
    const validDetails = extraDetails.filter(d => d.type && d.value);
    if (validDetails.length > 0) {
      const detailsText = validDetails.map(d => `${d.type}: ${d.value}`).join('; ');
      label += ` — [${detailsText}]`;
    }

    return label || zone.auto_label || zone.zone_name;
  };

  // Extra details management functions (already defined above at line 6813)

  const isFormValid = () => {
    // Check if hierarchical location data is valid
    const hierarchy = formData.location_hierarchy;
    const hasRequiredFields = hierarchy &&
      hierarchy.location_type &&
      hierarchy.location_type.trim() &&
      hierarchy.location_value &&
      hierarchy.location_value.trim();

    // Check for unfinished rows (Save Guard)
    const hasNoUnfinishedRows = !hierarchyValidationState.hasUnfinishedRows;

    return hasRequiredFields && hasNoUnfinishedRows;
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'normal': return 'bg-green-100 text-green-800';
      case 'maintenance': return 'bg-yellow-100 text-yellow-800';
      case 'closed': return 'bg-red-100 text-red-800';
      case 'restricted': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const findNodeById = (node, id) => {
    if (!node || !id) return null;
    if (node.id && node.id == id) return node;
    const children = node.details || node.subdetails || [];
    for (const child of children) {
      const found = findNodeById(child, id);
      if (found) return found;
    }
    return null;
  };

  const updateNodeField = (nodeId, fieldName, newValue) => {
    // Deep clone hierarchy to avoid mutating props
    const value = formData?.location_hierarchy;
    const updatedHierarchy = JSON.parse(JSON.stringify(value || {}));
    const target = findNodeById(updatedHierarchy, nodeId);

    if (target) {
      // Support both shapes: node may use { type, value } or { location_type, location_value }
      if (fieldName === 'location_type') {
        if (typeof target.type !== 'undefined') target.type = newValue;
        else target.location_type = newValue;
      } else if (fieldName === 'location_value') {
        if (typeof target.value !== 'undefined') target.value = newValue;
        else target.location_value = newValue;
      } else {
        // generic assign
        target[fieldName] = newValue;
      }

      // Update local form state
      setFormData({ ...formData, location_hierarchy: updatedHierarchy });
      return;
    }

    // fallback: update top-level fields if node not found
    setFormData({
      ...formData, location_hierarchy: {
        ...value,
        [fieldName]: newValue
      }
    });
  };

  function isDifferent(obj1, obj2) {
    // If both are identical (same reference), no difference
    if (obj1 === obj2) return false;

    // If types differ -> difference
    if (typeof obj1 !== typeof obj2) return true;

    // If value is primitive
    if (obj1 === null || obj2 === null || typeof obj1 !== "object") {
      return obj1 !== obj2;
    }

    // If arrays
    if (Array.isArray(obj1) && Array.isArray(obj2)) {
      if (obj1.length !== obj2.length) return true;

      for (let i = 0; i < obj1.length; i++) {
        if (isDifferent(obj1[i], obj2[i])) return true;
      }
      return false;
    }

    // If objects
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return true;

    for (let key of keys1) {
      if (!keys2.includes(key)) return true;
      if (isDifferent(obj1[key], obj2[key])) return true;
    }

    return false;
  }

  const handleSaveAndContinue = async () => {
    try {
      const isSave = await handleSave();
      if (isSave) {
        editZoneLocation(showUnsavedModal.data);
        setShowUnsavedModal({ toggle: false, data: {} });
      } else {
        setShowUnsavedModal({ ...showUnsavedModal, toggle: false })
      }
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    if (selectedTreeNodeId && displayedZones && displayedZones.length > 0) {
      const selectedTreeNodeLocation = displayedZones?.find(fi => fi.id == selectedTreeNodeId);
      editZone(selectedTreeNodeLocation);
      setSelectedTreeNodeId('');
    }
  }, [selectedTreeNodeId]);

  useEffect(() => {
    if (selectedZone && selectedZone.parent_id) {
      // Find all ancestor parent_ids
      const ancestors = new Set();
      let currentParentId = selectedZone.parent_id;
      
      // Build a lookup map of zones by id
      const zoneMap = new Map(zones.map(z => [z.id, z]));
      
      while (currentParentId && zoneMap.has(currentParentId)) {
        ancestors.add(currentParentId);
        const parentZone = zoneMap.get(currentParentId);
        currentParentId = parentZone?.parent_id;
      }
      
      if (ancestors.size > 0) {
        setExpandedParentIds(prev => {
          const next = new Set(prev);
          ancestors.forEach(id => next.add(id));
          return next;
        });
      }
    }
  }, [selectedZone, zones]);



  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {/* Main Content */}
      <div className="flex-1 flex relative">
        {/* Left Panel - Zones List */}
        <div className="flex-1 p-6" style={{ marginRight: `${rightPanelWidth + 4}px` }}>
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Locations & Zones</h2>
            <p className="text-sm text-gray-600 mb-4">Manage locations and zones as the authoritative physical model across DisasterMS. Click any zone to view and edit its details.</p>

            {/* ENHANCED SEARCH: Powerful fuzzy search with multi-token support */}
            <div className="flex items-center space-x-4 mb-6">
              <div className="relative flex-1 max-w-2xl">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  id="locations-search"
                  placeholder="Search zones by name, building, floor, status, department... (try 'status:normal' or 'dept:radiology')"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="pl-10 pr-10"
                />
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              <Button
                onClick={resetForm}
                className="bg-green-600 hover:bg-green-700 text-white flex-shrink-0"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add New Zone
              </Button>

              {searchQuery && (
                <div className="text-sm text-gray-500 flex-shrink-0">
                  {displayedZones.length} result{displayedZones.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>

            {/* Search Tips */}
            {searchQuery && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-700">
                  <span className="font-medium">Search Tips:</span> Try "status:normal", "building:main", "dept:radiology", "floor:3" or combine terms like "icu floor 2"
                </p>
              </div>
            )}
          </div>

          {/* Search Results / Empty State */}
          {searchQuery && displayedZones.length === 0 && (
            <div className="text-center py-12">
              <Search className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No matches found</h3>
              <p className="text-gray-500 mb-4">
                No locations match "{searchQuery}". Try adjusting your search terms.
              </p>
              <Button variant="outline" onClick={clearSearch}>
                <X className="w-4 h-4 mr-2" />
                Clear Search
              </Button>
            </div>
          )}

          {/* Bulk Selection Controls */}
          {displayedZones.length > 0 && (
            <div className="flex items-center justify-between mb-4 p-3 bg-gray-50 rounded-lg border">
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isSelectAllChecked}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Select All ({displayedZones.length})
                  </span>
                </label>

                {selectedLocationIds.size > 0 && (
                  <div className="text-sm text-blue-600 font-medium">
                    {selectedLocationIds.size} selected
                  </div>
                )}
              </div>

              {selectedLocationIds.size > 0 && (
                <div className="flex gap-2">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleBulkDelete}
                    className="flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete Selected ({selectedLocationIds.size})
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedLocationIds(new Set());
                      setIsSelectAllChecked(false);
                    }}
                  >
                    Clear Selection
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Zone Cards List - Hierarchical Tree Layout */}
          {(!searchQuery || displayedZones.length > 0) && (
            <div className="space-y-4 max-h-[calc(100vh-300px)] overflow-y-auto pb-6">
              {buildHierarchy(displayedZones).map((rootNode) => (
                <LocationCardNode
                  key={rootNode.id}
                  node={rootNode}
                  depth={0}
                  selectedZone={selectedZone}
                  selectedLocationIds={selectedLocationIds}
                  handleLocationSelect={handleLocationSelect}
                  editZone={editZone}
                  setDeleteModal={setDeleteModal}
                  expandedParentIds={expandedParentIds}
                  toggleExpand={toggleExpand}
                  getStatusBadgeColor={getStatusBadgeColor}
                />
              ))}
            </div>
          )}
        </div>

        {/* Resizable Divider */}
        <div
          className="w-1 bg-gray-300 hover:bg-blue-400 cursor-col-resize transition-colors flex-shrink-0 relative group"
          onMouseDown={handleMouseDown}
          style={{ position: 'absolute', right: `${rightPanelWidth}px`, top: 0, bottom: 0, zIndex: 10 }}
        >
          <div className="absolute inset-y-0 -left-1 -right-1 group-hover:bg-blue-400 group-hover:opacity-50"></div>
        </div>

        {/* Right Panel - Zone Editor / Department Management */}
        <div
          className="bg-white shadow-lg p-6 overflow-y-auto max-h-[calc(100vh-81px)] border-l border-gray-200 absolute right-0 top-0 bottom-0"
          style={{ width: `${rightPanelWidth}px` }}
        >
          {/* Conditionally render either Location Form or Department Management */}
          {showDepartmentManagement && selectedZone ? (
            <LocationDepartmentManagement
              selectedZone={selectedZone}
              onBack={() => setShowDepartmentManagement(false)}
              managedLocationId={managedLocationId}
              associatedDepartments={associatedDepartments}
              setAssociatedDepartments={setAssociatedDepartments}
              setZones={setZones}
              fetchLocationDepartmentCounts={fetchLocationDepartmentCounts}
              updateZoneCounts={updateZoneCounts}
              updateLocationCardCounts={updateLocationCardCounts}
              fetchAssociatedDepartments={fetchAssociatedDepartments}
              isLoadingDepartments={isLoadingDepartments}
              departmentSearchQuery={departmentSearchQuery}
              setDepartmentSearchQuery={setDepartmentSearchQuery}
              isAddingDepartment={isAddingDepartment}
              addDepartmentToLocation={addDepartmentToLocation}
              removeDepartmentFromLocation={removeDepartmentFromLocation}
              updateDepartmentContact={updateDepartmentContact}
            />
          ) : (
            <>
              <h3 className="text-lg font-semibold mb-4">
                {isEditing ?
                  (formData._hierarchy_meta?.root_label ?
                    `Edit Location: ${formData._hierarchy_meta.root_label}` :
                    "Edit Location Zone"
                  ) :
                  "New Location Zone"
                }
              </h3>

              <div className="space-y-6" onKeyPress={handleKeyPress}>
                {/* Location Hierarchy */}
                <div>
                  <LocationHierarchyManager
                    value={formData.location_hierarchy}
                    onChange={(value) => setFormData({ ...formData, location_hierarchy: value })}
                    className="mt-2"
                    // Drive the hierarchy manager focus/highlight from the explicit selectedZone (clicked card).
                    // Fallback to server-provided target_chip_id when selectedZone is not available.
                    focusChipId={selectedZone?.id || formData._hierarchy_meta?.target_chip_id}
                    onValidationChange={setHierarchyValidationState}
                    key={`hierarchy-manager-${suggestionRefreshCounter}`}  // Force remount when suggestions should refresh
                    formData={formData}
                    attachments={attachments}
                    setAttachments={setAttachments}
                    selectedZone={selectedZone}
                    setSelectedTreeNodeId={setSelectedTreeNodeId}
                    displayedZones={displayedZones}
                    setIsVailid={setIsVailid}
                    isVailid={isVailid}
                    gpsPicker={
                      /* GPS Location picker (map pin + Google search) — rendered
                         inside the hierarchy manager, below the status select */
                      <ZoneGpsPicker
                        value={(() => {
                          const editingNodeId = formData?._hierarchy_meta?.editing_node_id || formData?._hierarchy_meta?.target_chip_id || null;
                          const value = formData?.location_hierarchy;
                          if (editingNodeId) {
                            const node = findNodeById(value, editingNodeId);
                            return (node && node.gps) || null;
                          }
                          return value?.gps || null;
                        })()}
                        onChange={(gps) => {
                          const editingNodeId = formData?._hierarchy_meta?.editing_node_id || formData?._hierarchy_meta?.target_chip_id || null;
                          if (editingNodeId) {
                            updateNodeField(editingNodeId, 'gps', gps);
                          } else {
                            setFormData((prev) => ({ ...prev, location_hierarchy: { ...(prev.location_hierarchy || {}), gps } }));
                          }
                        }}
                      />
                    }
                  />
                </div>


                {/* Description */}
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <Textarea
                    // value={formData.description}
                    // onChange={(e) => setFormData({...formData, description: e.target.value})}
                    value={(() => {
                      const editingNodeId = formData?._hierarchy_meta?.editing_node_id || formData?._hierarchy_meta?.target_chip_id || null;
                      const value = formData?.location_hierarchy;
                      if (editingNodeId) {
                        const node = findNodeById(value, editingNodeId);
                        return (node && (node?.description || ''));
                      }
                      return formData?.description || value?.description || '';
                    })()}
                    onChange={(e) => {
                      const editingNodeId = formData?._hierarchy_meta?.editing_node_id || formData?._hierarchy_meta?.target_chip_id || null;
                      const newDesc = e.target.value;
                      if (editingNodeId) {
                        updateNodeField(editingNodeId, 'description', newDesc);
                      } else {
                        setFormData({ ...formData, description: e.target.value });
                      }
                    }}
                    placeholder="Optional description..."
                    disabled={isSubmitting}
                    className="border-gray-300 resize-none"
                    rows={3}
                  />
                </div>

              </div>
              {!isFormValid() && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700 font-medium">
                    ⚠️ Please complete all required fields
                  </p>
                  <div className="text-xs text-red-600 mt-1 space-y-1">
                    {(!formData.location_hierarchy?.location_type?.trim()) && <p>• Location Type is required</p>}
                    {(!formData.location_hierarchy?.location_value?.trim()) && <p>• Location Value is required</p>}
                    {hierarchyValidationState.hasUnfinishedRows && (
                      <p>• {hierarchyValidationState.unfinishedRowsMessage}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Error Display */}
              {submitError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700 font-medium">
                    ❌ Failed to save zone
                  </p>
                  <p className="text-xs text-red-600 mt-1">
                    {typeof submitError === 'object' ? JSON.stringify(submitError) : submitError}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 rounded-md font-medium border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                  style={{ minHeight: '40px' }}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!isFormValid() || isSubmitting || isVailid}
                  className={`flex-1 px-4 py-2 rounded-md font-medium transition-colors ${isFormValid() && !isSubmitting && !isVailid
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-gray-400 cursor-not-allowed opacity-60 text-gray-200'
                    }`}
                  style={{ minHeight: '40px' }}
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {isEditing ? "Updating..." : "Saving..."}
                    </div>
                  ) : (
                    isEditing ? "Update Zone" : "💾 Save Zone"
                  )}
                </button>
              </div>

              {/* Manage Associated Departments Button - Show when editing or after successful save */}
              {/* {selectedZone && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowDepartmentManagement(true);
                    openDepartmentManagement(selectedZone);
                  }}
                  className="w-full px-4 py-3 rounded-md font-medium text-white bg-green-600 hover:bg-green-700 transition-colors flex items-center justify-center"
                >
                  <Building className="w-4 h-4 mr-2" />
                  Manage Associated Departments
                </button>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Manage which departments are associated with this location
                </p>
              </div>
            )} */}
            </>
          )}
        </div>
      </div>
      {/* Unsaved changes modal */}
      {showUnsavedModal.toggle && (
        <Dialog
          open={showUnsavedModal.toggle}
          onOpenChange={(open) => setShowUnsavedModal({ ...showUnsavedModal, toggle: false })}
        >
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>You have unsaved changes</DialogTitle>
              <DialogDescription>
                You have unsaved changes for the current location. Do you want to save them before navigating?
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4 flex gap-2">
              <button
                onClick={() => {
                  handleSaveAndContinue();
                }}
                className="flex-1 px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {isSubmitting ? "Saving..." : "Save and continue"}
              </button>

              <button
                onClick={() => {
                  editZoneLocation(showUnsavedModal.data);
                  setShowUnsavedModal({ toggle: false, data: {} });
                }}
                className="flex-1 px-4 py-2 rounded-md bg-gray-200 text-gray-800 hover:bg-gray-300"
              >
                Discard changes and continue
              </button>

              <button
                onClick={() => setShowUnsavedModal({ ...showUnsavedModal, toggle: false })}
                className="flex-1 px-4 py-2 rounded-md bg-white border border-gray-300 text-gray-800 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Small delete confirmation modal using AlertDialog */}
      {deleteModal.open && (
        <AlertDialog open={deleteModal.open} onOpenChange={(open) => {
          if (!open) setDeleteModal({ open: false, id: null, name: '' });
        }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete location and zone</AlertDialogTitle>
              <AlertDialogDescription>
                {`"${deleteModal.name}" location has child entries.
                    Deleting it will permanently remove all nested locations under it.
                    Do you want to proceed?`}

              </AlertDialogDescription>
            </AlertDialogHeader>

            <div className="mt-4 flex gap-2">
              <AlertDialogCancel onClick={() => setDeleteModal({ open: false, id: null, name: '' })}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction onClick={async () => {
                try {
                  await handleDelete(deleteModal.id);
                } finally {
                  setDeleteModal({ open: false, id: null, name: '' });
                }
              }}>
                Delete
              </AlertDialogAction>
            </div>
          </AlertDialogContent>
        </AlertDialog>
      )}

    </div>
  );
};



export default LocationsAndZones;
